import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bodies, Body, Composite, Engine } from 'matter-js';
import type { Body as MatterBody } from 'matter-js';
import QRCode from 'qrcode';
import {
  ArrowLeft,
  BookMarked,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  AlertCircle,
  Gift,
  KeyRound,
  LockKeyhole,
  Menu,
  Minus,
  Moon,
  Plus,
  ReceiptText,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  Type,
  WalletCards,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import UserAccountMenu from '../components/UserAccountMenu';
import { antiBrushHeaders } from '../utils/analytics';
import { formatInkBalance } from '../utils/ink';
import { setSeoMetadata } from '../utils/seo';
import './BookstorePage.css';

interface SitePageProps {
  onLogin: () => void;
  onRegister: () => void;
  onWorkbench: () => void;
  onLibrary: () => void;
  onHome: () => void;
  onAccount: () => void;
  onOrders: () => void;
  onBookshelf: () => void;
  onProfile: () => void;
}

interface StoredUser {
  id: number;
  username: string;
  email?: string | null;
  nickname?: string | null;
  avatar_url?: string | null;
}

interface InkAccount {
  balance: number;
  frozen: number;
  member_plan: string;
  member_name: string;
  member_expires_at?: string | null;
  monthly_quota: number;
  monthly_used: number;
}

interface InkRecord {
  id: string;
  type: string;
  amount: number;
  balance_after: number;
  operator: string;
  reason: string;
  source: string;
  createdAt: string;
  cost_yuan?: number;
  model?: string;
  cost_source?: string;
}

interface AssetOrder {
  id: string;
  product: string;
  channel: string;
  paymentUrl?: string;
  amount: number;
  ink: number;
  status: 'paid' | 'pending' | 'refunded' | 'closed' | string;
  paidAt: string;
  source: string;
  canRefund?: boolean;
  refundStatus?: string;
  refundReason?: string;
  refundRequestedAt?: string;
  refundProcessedAt?: string;
  refundRemark?: string;
}

interface UserAssets {
  account: InkAccount;
  records: InkRecord[];
  orders: AssetOrder[];
  project_count: number;
}

interface UserGenerationSettings {
  max_chapters: number;
  default_chapters: number;
  system_default_chapters: number;
  uses_personal_default: boolean;
}

interface ReaderAdvertisementSettings {
  enabled: boolean;
  provider: string;
  left_enabled: boolean;
  left_image_url: string;
  left_target_url: string;
  left_ad_unit_id: string;
  left_alt: string;
  right_enabled: boolean;
  right_image_url: string;
  right_target_url: string;
  right_ad_unit_id: string;
  right_alt: string;
}

const disabledReaderAdvertisements: ReaderAdvertisementSettings = {
  enabled: false,
  provider: 'pending',
  left_enabled: true,
  left_image_url: '',
  left_target_url: '',
  left_ad_unit_id: '',
  left_alt: '左侧广告',
  right_enabled: true,
  right_image_url: '',
  right_target_url: '',
  right_ad_unit_id: '',
  right_alt: '右侧广告',
};

interface RechargeProduct {
  id: string;
  name: string;
  amount: number;
  payAmount: number;
  unit: string;
  description: string;
  featured?: boolean;
  channels: string[];
}

interface ShelfBook {
  slug: string;
  title: string;
  author: string;
  reader: string;
  category: string;
  words: string;
  status: string;
  update: string;
  cover: string;
  desc: string;
  tags: string[];
  latestChapter: string;
  intro: string;
  authorBio: string;
  avatar: string;
  paywall?: {
    enabled: boolean;
    paid_from_chapter: number;
    chapter_price: number;
  };
  chapterCount?: number;
  chapters: ShelfChapter[];
}

interface ShelfChapter {
  no: number;
  title: string;
  locked?: boolean;
  price?: number;
  unlocked?: boolean;
  words: number;
  updatedAt: string;
  content: string[];
}

interface LibraryRankingItem {
  id: string;
  rank: number;
  channel?: LibraryRankingChannel;
  slug: string;
  title: string;
  status: string;
  update: string;
  author?: string;
  category?: string;
  cover?: string;
  cover_url?: string;
}

interface LibraryFeatureItem {
  id: string;
  sort: number;
  label: string;
  slug: string;
  title: string;
  author: string;
  status: string;
  cover: string;
}

type LibraryRankingChannel = '男频' | '女频' | '新书';
const libraryRankingChannels: LibraryRankingChannel[] = ['男频', '女频', '新书'];

interface SiteNewsItem {
  id: string;
  date: string;
  title: string;
  desc: string;
  content?: string;
}

interface LibraryCategoryItem {
  id: string;
  sort: number;
  channel: '男频' | '女频';
  name: string;
}

interface Campaign {
  id: string;
  type: string;
  nav_label: string;
  phase_label: string;
  badge: string;
  headline_prefix: string;
  headline_main: string;
  original_price: string;
  current_price: string;
  countdown_label: string;
  countdown_value: string;
  win_rate: string;
  prize_title: string;
  prize_price: string;
  features: string[];
  participant_text: string;
  claim_note: string;
  claim_source: string;
}

interface ActiveCampaignResponse {
  active: boolean;
  campaign: Campaign | null;
}

interface LibraryUpdateItem {
  id: string;
  slug: string;
  chapterNo: number;
  type: string;
  title: string;
  chapter: string;
  author: string;
  updatedAt: string;
}

interface LibraryFilters {
  reader: string;
  category: string;
  status: string;
  sort: 'hot' | 'latest';
  keyword: string;
}

export type BillingCycle = 'month' | 'year';

export interface PricingPlan {
  id: 'free' | 'plus' | 'studio';
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  subtitle: string;
  yearlySubtitle: string;
  detail: string;
  yearlyDetail: string;
  features: string[];
  featured?: boolean;
  cta: string;
}

const bookCover = (seed: string) => `https://picsum.photos/seed/${encodeURIComponent(seed)}/180/240`;
const authorAvatar = (seed: string) => `https://api.dicebear.com/9.x/avataaars/svg?seed=${encodeURIComponent(seed)}&backgroundColor=f7ede2,d1fae5,e0f2fe`;

export const pricingPlans: PricingPlan[] = [
  {
    id: 'free',
    name: '轻量补给',
    monthlyPrice: 19,
    yearlyPrice: 19,
    subtitle: '到账 190 墨水',
    yearlySubtitle: '到账 190 墨水',
    detail: '适合临时补几章、改大纲和 AI 助手问答。',
    yearlyDetail: '按实际 token 用量扣减，不包含包月权益。',
    features: ['190 墨水即时到账', '按量结算', 'AI 助手与章节生成', '消费流水可查'],
    cta: '购买墨水',
  },
  {
    id: 'plus',
    name: '畅写补给',
    monthlyPrice: 59,
    yearlyPrice: 59,
    subtitle: '到账 590 墨水',
    yearlySubtitle: '到账 590 墨水',
    detail: '适合稳定连载前期，按真实消耗补给成本。',
    yearlyDetail: '按实际 token 用量扣减，不包含包年权益。',
    features: ['590 墨水即时到账', '适合连续章节生成', 'AI 审稿与记忆提取', '消费流水可查'],
    featured: true,
    cta: '立即购买',
  },
  {
    id: 'studio',
    name: '工作室包',
    monthlyPrice: 199,
    yearlyPrice: 199,
    subtitle: '到账 1,990 墨水',
    yearlySubtitle: '到账 1,990 墨水',
    detail: '适合团队批量测试题材和长篇章节生成。',
    yearlyDetail: '按实际 token 用量扣减，不包含包年权益。',
    features: ['1,990 墨水即时到账', '适合长篇与批量创作', 'AI 封面与后期工具', '消费流水可查'],
    cta: '购买墨水',
  },
];

export const findPricingPlan = (planId?: string | null) =>
  pricingPlans.find((plan) => plan.id === planId) || pricingPlans[1];

const defaultApiUrl = window.location.hostname === 'localhost' ? 'http://localhost:65181' : window.location.origin;
const defaultApiVersion = window.location.hostname === 'localhost' ? '/api/v1' : '/api/web';
const apiBaseUrl = `${process.env.REACT_APP_API_URL || defaultApiUrl}${process.env.REACT_APP_API_VERSION === undefined ? defaultApiVersion : process.env.REACT_APP_API_VERSION}`;
const goPayment = async (planId: PricingPlan['id'], _cycle: BillingCycle = 'month') => {
  if (!localStorage.getItem('lingbi-token')) {
    return;
  }
  void planId;
  window.location.hash = '#account';
};

const authHeaders = () => {
  const token = localStorage.getItem('lingbi-token');
  return {
    'Content-Type': 'application/json',
    ...antiBrushHeaders(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const apiRequest = async <T,>(path: string, options: RequestInit = {}): Promise<T> => {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.detail || data?.message || '请求失败');
  }
  return data as T;
};

const useActiveCampaign = () => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadCampaign = async () => {
      try {
        const result = await apiRequest<ActiveCampaignResponse>('/campaigns/active');
        if (!cancelled) setCampaign(result.active && result.campaign ? result.campaign : null);
      } catch {
        if (!cancelled) setCampaign(null);
      }
    };
    void loadCampaign();
    return () => {
      cancelled = true;
    };
  }, []);

  return campaign;
};

const emptyAssets: UserAssets = {
  account: {
    balance: 0,
    frozen: 0,
    member_plan: 'free',
    member_name: '体验用户',
    member_expires_at: null,
    monthly_quota: 1,
    monthly_used: 0,
  },
  records: [],
  orders: [],
  project_count: 0,
};

const chapterTitles = [
  '旧钟在三点零八分停下',
  '雨夜来客',
  '被擦掉的名字',
  '第一份契约',
  '镜中第二次醒来',
  '城南档案室',
  '不能回头的楼梯',
  '白塔下的交易',
  '有人提前写好了结局',
  '火车抵达天亮之前',
  '欠下的一分钟',
  '终章之前的门',
];

const buildChapters = (bookTitle: string, seed: string): ShelfChapter[] =>
  chapterTitles.map((title, index) => {
    const no = index + 1;
    return {
      no,
      title: `第${no}章 ${title}`,
      locked: false,
      words: 2800 + no * 120,
      updatedAt: no < 10 ? `2026-06-0${no}` : `2026-06-${no}`,
      content: [
        `${bookTitle}的故事从一个不合时宜的细节开始。主角站在熟悉又陌生的街口，听见远处传来钟声，才意识到昨夜留下的线索并没有消失，只是换了一种方式等待他重新看见。`,
        `他把纸页摊开，旧墨水在灯下泛出微光。每一个名字后面都有被刻意抹去的空白，而这些空白恰好连成一条通往真相的路。`,
        `风从窗缝里钻进来，卷起桌上的尘。有人在门外轻轻敲了三下，节奏和梦里完全一致。主角没有立刻开门，因为他终于明白，自己不是被命运选中，而是正在和命运谈判。`,
        `这一章末尾，他决定把最后一枚筹码押上去。无论门后站着的是敌人、旧友，还是另一个自己，他都必须走进下一场局。`,
      ].map((paragraph, paragraphIndex) => `${paragraph} ${seed.slice(0, 2)}-${no}-${paragraphIndex + 1}`),
    };
  });

const rawShelfBooks = [
  { slug: 'clock-god', title: '时停起手，邪神也得给我跪下', author: '六个葫芦', reader: '男生', category: '主题', words: '196.4万字', status: '连载中', update: '15小时前', cover: bookCover('clock-god'), desc: '时间静止、末世、异能与旧神复苏交错，主角在每次停表后付出代价。' },
  { slug: 'not-drama-god', title: '我不是戏神', author: '三九音域', reader: '男生', category: '角色', words: '400.4万字', status: '已完结', update: '2026-04-30 20:28', cover: bookCover('not-drama-god'), desc: '斩神作者全新力作，舞台、命运和旧剧本不断反转。' },
  { slug: 'princess-reborn', title: '睁眼！太子爷的恶毒女配重生了', author: '檐上歌', reader: '女生', category: '情节', words: '34.3万字', status: '连载中', update: '10小时前', cover: bookCover('princess-reborn'), desc: '恋爱脑醒来之后，她发现豪门棋局里每个人都在演。' },
  { slug: 'ten-day-end', title: '十日终焉', author: '杀虫队队员', reader: '男生', category: '主题', words: '320.1万字', status: '已完结', update: '2025-10-31 22:10', cover: bookCover('ten-day-end'), desc: '番茄年度巅峰榜作品，规则怪谈和生死局层层推进。' },
  { slug: 'game-god-path', title: '这个游戏不对劲，我挖出了成神路', author: '道不易', reader: '男生', category: '情节', words: '125.1万字', status: '连载中', update: '1小时前', cover: bookCover('game-god-path'), desc: '职业打金人发现副本背后藏着真实世界的成神路径。' },
  { slug: 'game-invasion', title: '游戏入侵：抢男女主机缘会上瘾诶', author: '猫不秃', reader: '女生', category: '角色', words: '313万字', status: '已完结', update: '2026-05-31 06:01', cover: bookCover('game-invasion'), desc: '世界游戏化后，她专抢剧情主角的关键机缘。' },
  { slug: 'hospital-god', title: '我在精神病院学斩神', author: '三九音域', reader: '男生', category: '主题', words: '426.4万字', status: '已完结', update: '2024-07-31 09:55', cover: bookCover('hospital-god'), desc: '精神病院里的神明课堂，把少年推向守夜人与神战。' },
  { slug: 'sword-coffin', title: '灵墟，剑棺，瞎剑客', author: '煮熟的来福鸽', reader: '男生', category: '角色', words: '775.7万字', status: '连载中', update: '4小时前', cover: bookCover('sword-coffin'), desc: '剑道、灵墟和命格反噬组成高密度升级长线。' },
  { slug: 'rain-field', title: '嘉豪就变强？操场雨中开大了', author: '落叶之刃', reader: '男生', category: '情节', words: '27.2万字', status: '连载中', update: '10小时前', cover: bookCover('rain-field'), desc: '评分刚出，包涨的校园异能爽文，节奏轻快。' },
  { slug: 'golden-immortal', title: '我都大罗金仙了，你们才武圣', author: '飞云脚', reader: '男生', category: '主题', words: '94.7万字', status: '连载中', update: '22小时前', cover: bookCover('golden-immortal'), desc: '西游、聊天群和加点流混合，轻松反差感强。' },
  { slug: 'sky-abyss', title: '天渊', author: '沐潇三生', reader: '男生', category: '情节', words: '574.7万字', status: '连载中', update: '12小时前', cover: bookCover('sky-abyss'), desc: '百年前旧约重启，陈青源从天渊归来。' },
  { slug: 'royal-county', title: '皇家金牌县令', author: '板面王仔', reader: '男生', category: '角色', words: '272.3万字', status: '已完结', update: '2025-05-20 21:07', cover: bookCover('royal-county'), desc: '种田、轻松娱乐和穿越县令组合，读感松弛。' },
  { slug: 'abnormal-749', title: '大一实习，你跑去749收容异常', author: '我爱罗的沙', reader: '男生', category: '主题', words: '331.2万字', status: '连载中', update: '16小时前', cover: bookCover('abnormal-749'), desc: '异常收容、实习生和都市怪物档案，开篇抓人。' },
  { slug: 'rule-system', title: '规则怪谈：无所谓，系统会出手', author: '巡山主神', reader: '男生', category: '情节', words: '88.7万字', status: '连载中', update: '12小时前', cover: bookCover('rule-system'), desc: '怪谈副本里没挂别来，系统会替主角兜底。' },
  { slug: 'chief-minister', title: '魏家孽种成首辅，全族跪求我认祖', author: '生活中的咸鱼', reader: '男生', category: '角色', words: '65.8万字', status: '连载中', update: '21小时前', cover: bookCover('chief-minister'), desc: '被弃少年科举逆袭，家族悔恨和权谋线并行。' },
];

export const shelfBooks: ShelfBook[] = rawShelfBooks.map((book, index) => ({
  ...book,
  tags: [book.reader, book.category, book.status, index % 2 === 0 ? '高能爽点' : '强情绪'],
  latestChapter: `第${8 + (index % 5)}章 ${chapterTitles[7 + (index % 5)]}`,
  intro: `${book.desc} 故事以强钩子切入，兼顾人物成长、阶段反转和长线悬念，适合在书阁详情页继续浏览目录并进入阅读。`,
  authorBio: `${book.author} 擅长把清晰设定、连续冲突和短章节节奏组合起来，作品更新稳定，读感轻快。`,
  avatar: authorAvatar(book.author),
  chapters: buildChapters(book.title, book.slug),
}));

const findShelfBookExact = (slug?: string | null) =>
  shelfBooks.find((book) => book.slug === slug);

const createLibraryPlaceholder = (slug?: string | null): ShelfBook => ({
  slug: slug || 'loading',
  title: '正在同步书阁作品...',
  author: '灵笔书阁',
  reader: '原创',
  category: '原创',
  words: '同步中',
  status: '已上架',
  update: '',
  cover: bookCover(`published-${slug || 'loading'}`),
  desc: '正在从书阁同步作品详情。',
  tags: ['灵笔书阁', '同步中'],
  latestChapter: '同步中',
  intro: '正在从书阁同步作品详情。',
  authorBio: '作者资料正在同步。',
  avatar: authorAvatar(slug || '灵笔书阁'),
  chapters: [],
});

const fallbackLibraryRankings: LibraryRankingItem[] = shelfBooks.slice(0, 10).map((book, index) => ({
  id: `rank-${index + 1}`,
  rank: index + 1,
  channel: book.reader === '女生' ? '女频' : index >= 7 ? '新书' : '男频',
  slug: book.slug,
  title: book.title,
  status: book.status,
  update: book.update,
}));

const fallbackLibraryFeatures: LibraryFeatureItem[] = [];

const fallbackSiteNews: SiteNewsItem[] = [
  { id: 'news-1', date: '06-09', title: '灵笔书阁作品审核规则上线', desc: '站内展示作品需通过基础质量、版权与敏感内容审核。' },
  { id: 'news-2', date: '06-08', title: 'AI 原创专区首批科幻与悬疑作品开放', desc: 'AI 辅助标识透明展示，排序仍以作品质量为先。' },
  { id: 'news-3', date: '06-07', title: '作者工作台发布链路进入灰度', desc: '作者可以从灵笔工作台发布作品到书阁，运营审核后展示。' },
  { id: 'news-4', date: '06-06', title: '墨水账户与福利领取记录上线', desc: '福利领取、后台划转和充值记录均可在账户中心查看。' },
];

// 与用户端资讯接口的上限保持一致，避免后台已发布的资讯在前端被二次截断。
const SITE_NEWS_DISPLAY_LIMIT = 9;

const fallbackLibraryUpdates: LibraryUpdateItem[] = [
  { id: 'fallback-update-1', slug: 'clock-god', chapterNo: 1, type: '科幻', title: '星海归墟纪', chapter: '第 214 章 归墟信标', author: '江潮未眠', updatedAt: '10:42' },
  { id: 'fallback-update-2', slug: 'clock-god', chapterNo: 1, type: '古言', title: '长安雨未歇', chapter: '第 67 章 旧宫门', author: '山月知秋', updatedAt: '09:18' },
  { id: 'fallback-update-3', slug: 'clock-god', chapterNo: 1, type: '无限流', title: '无限副本管理员', chapter: '第 39 章 维修权限', author: '北港栈桥', updatedAt: '昨天' },
  { id: 'fallback-update-4', slug: 'clock-god', chapterNo: 1, type: '悬疑', title: '城北旧案录', chapter: '第 28 章 黑色胶片', author: '顾临川', updatedAt: '昨天' },
];

const applyLibraryFilters = (items: ShelfBook[], filters: LibraryFilters) => {
  const keyword = filters.keyword.trim().toLowerCase();
  return items
    .filter((book) => filters.reader === '全部' || book.reader === filters.reader)
    .filter((book) => filters.category === '全部' || book.category === filters.category)
    .filter((book) => filters.status === '全部' || book.status === filters.status)
    .filter((book) => {
      if (!keyword) return true;
      return [book.title, book.author, book.desc, book.category].join(' ').toLowerCase().includes(keyword);
    })
    .sort((a, b) => {
      if (filters.sort === 'latest') return String(b.update).localeCompare(String(a.update), 'zh-Hans-CN');
      return (b.chapterCount ?? b.chapters.length) - (a.chapterCount ?? a.chapters.length);
    });
};

const mergeShelfBooks = (published: ShelfBook[], filters: LibraryFilters) => {
  const slugs = new Set(published.map((book) => book.slug));
  const demoBooks = shelfBooks.filter((book) => !slugs.has(book.slug));
  const filteredPublished = applyLibraryFilters(published, filters);
  const filteredDemo = applyLibraryFilters(demoBooks, filters);
  return [...filteredPublished, ...filteredDemo];
};

const readStoredUser = (): StoredUser | null => {
  try {
    const raw = localStorage.getItem('lingbi-user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const useCurrentUser = () => {
  const [currentUser, setCurrentUser] = useState<StoredUser | null>(readStoredUser);
  useEffect(() => {
    const syncUser = () => setCurrentUser(readStoredUser());
    window.addEventListener('storage', syncUser);
    window.addEventListener('hashchange', syncUser);
    window.addEventListener('lingbi-auth-change', syncUser);
    return () => {
      window.removeEventListener('storage', syncUser);
      window.removeEventListener('hashchange', syncUser);
      window.removeEventListener('lingbi-auth-change', syncUser);
    };
  }, []);
  return [currentUser, setCurrentUser] as const;
};

const useUserAssets = (enabled: boolean) => {
  const [assets, setAssets] = useState<UserAssets>(emptyAssets);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    if (!enabled || !localStorage.getItem('lingbi-token')) {
      setAssets(emptyAssets);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setAssets(await apiRequest<UserAssets>('/users_app/me/assets'));
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载资产失败');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { assets, loading, error, reload };
};

export const findShelfBook = (slug?: string | null) =>
  findShelfBookExact(slug) || shelfBooks[0];

const useLibraryBooks = (filters: LibraryFilters) => {
  const [publishedBooks, setPublishedBooks] = useState<ShelfBook[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.reader !== '全部') params.set('reader', filters.reader);
      if (filters.category !== '全部') params.set('category', filters.category);
      if (filters.status !== '全部') params.set('status', filters.status);
      if (filters.keyword.trim()) params.set('keyword', filters.keyword.trim());
      params.set('sort', filters.sort);
      const query = params.toString();
      setPublishedBooks(await apiRequest<ShelfBook[]>(`/novel_project_app/library/books${query ? `?${query}` : ''}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : '书阁同步失败');
    } finally {
      setLoading(false);
    }
  }, [filters.category, filters.keyword, filters.reader, filters.sort, filters.status]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return {
    books: mergeShelfBooks(publishedBooks, filters),
    loading,
    error,
    reload,
  };
};

const useLibraryRankings = (channel: LibraryRankingChannel) => {
  const [rankings, setRankings] = useState<LibraryRankingItem[]>(() => fallbackLibraryRankings.filter((item) => (item.channel || '男频') === channel));

  useEffect(() => {
    let cancelled = false;
    const loadRankings = async () => {
      try {
        const result = await apiRequest<LibraryRankingItem[]>(`/novel_project_app/library/rankings?channel=${encodeURIComponent(channel)}`);
        if (!cancelled) setRankings(result.slice(0, 27));
      } catch {
        if (!cancelled) setRankings(fallbackLibraryRankings.filter((item) => (item.channel || '男频') === channel));
      }
    };
    void loadRankings();
    return () => {
      cancelled = true;
    };
  }, [channel]);

  return rankings;
};

const useLibraryFeatures = () => {
  const [features, setFeatures] = useState<LibraryFeatureItem[]>(fallbackLibraryFeatures);

  useEffect(() => {
    let cancelled = false;
    const loadFeatures = async () => {
      try {
        const result = await apiRequest<LibraryFeatureItem[]>('/novel_project_app/library/features');
        if (!cancelled) setFeatures(result.slice(0, 6));
      } catch {
        if (!cancelled) setFeatures(fallbackLibraryFeatures);
      }
    };
    void loadFeatures();
    return () => {
      cancelled = true;
    };
  }, []);

  return features;
};

const useSiteNews = () => {
  const [items, setItems] = useState<SiteNewsItem[]>(fallbackSiteNews);

  useEffect(() => {
    let cancelled = false;
    const loadNews = async () => {
      try {
        const result = await apiRequest<SiteNewsItem[]>('/novel_project_app/library/news');
        if (!cancelled && result.length) {
          setItems(result.slice(0, SITE_NEWS_DISPLAY_LIMIT));
        }
      } catch {
        if (!cancelled) setItems(fallbackSiteNews);
      }
    };
    void loadNews();
    return () => {
      cancelled = true;
    };
  }, []);

  return items;
};

const useLibraryUpdates = () => {
  const [items, setItems] = useState<LibraryUpdateItem[]>(fallbackLibraryUpdates);

  useEffect(() => {
    let cancelled = false;
    const loadUpdates = async () => {
      try {
        const result = await apiRequest<LibraryUpdateItem[]>('/novel_project_app/library/updates?limit=6');
        if (!cancelled && result.length) {
          setItems(result.slice(0, 6));
        }
      } catch {
        if (!cancelled) setItems(fallbackLibraryUpdates.slice(0, 6));
      }
    };
    void loadUpdates();
    return () => {
      cancelled = true;
    };
  }, []);

  return items;
};

const useLibraryCategories = () => {
  const [items, setItems] = useState<LibraryCategoryItem[]>([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await apiRequest<LibraryCategoryItem[]>('/novel_project_app/library/categories');
        if (!cancelled) setItems(result);
      } catch {
        if (!cancelled) setItems([]);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, []);
  return items;
};

const useLibraryBook = (slug?: string | null) => {
  const [book, setBook] = useState<ShelfBook>(() => findShelfBookExact(slug) || createLibraryPlaceholder(slug));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!slug) {
      setBook(findShelfBook(slug));
      return;
    }
    const fallback = findShelfBookExact(slug) || createLibraryPlaceholder(slug);
    setBook(fallback);
    let cancelled = false;
    const loadBook = async () => {
      setLoading(true);
      try {
        const result = await apiRequest<ShelfBook>(`/novel_project_app/library/books/${encodeURIComponent(slug)}`);
        if (!cancelled) setBook(result);
      } catch {
        if (!cancelled) setBook(fallback);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadBook();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { book, loading };
};

const useLibraryChapter = (slug?: string | null, chapterNo = 1, fallbackBook?: ShelfBook) => {
  const fallbackChapter = fallbackBook?.chapters.find((item) => item.no === chapterNo) || fallbackBook?.chapters[0] || buildChapters('灵笔作品', 'fallback')[0];
  const [chapter, setChapter] = useState<ShelfChapter>({ ...fallbackChapter, locked: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const nextFallback = fallbackBook?.chapters.find((item) => item.no === chapterNo) || fallbackBook?.chapters[0] || buildChapters('灵笔作品', 'fallback')[0];
    setChapter({ ...nextFallback, locked: false });
    if (!slug) return;
    let cancelled = false;
    const loadChapter = async () => {
      setLoading(true);
      try {
        const result = await apiRequest<ShelfChapter>(`/novel_project_app/library/books/${encodeURIComponent(slug)}/chapters/${chapterNo}`);
        if (!cancelled) setChapter(result);
      } catch {
        if (!cancelled) setChapter({ ...nextFallback, locked: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadChapter();
    return () => {
      cancelled = true;
    };
  }, [slug, chapterNo, fallbackBook]);

  return { chapter, loading, setChapter };
};

const goBookDetail = (slug: string) => {
  window.history.pushState(null, '', `/library/book/${encodeURIComponent(slug)}`);
  window.dispatchEvent(new Event('lingbi-route-change'));
};

const goReader = (slug: string, chapterNo = 1) => {
  window.history.pushState(null, '', `/library/read/${encodeURIComponent(slug)}/${chapterNo}`);
  window.dispatchEvent(new Event('lingbi-route-change'));
};

const getAvatarInitial = (value?: string | null) => {
  const text = (value || '灵').trim();
  return text.slice(0, 1).toUpperCase();
};

const UserAvatar: React.FC<{ user: StoredUser; className?: string }> = ({ user, className }) => {
  const label = user.nickname || user.username;
  if (user.avatar_url) {
    return <img className={className} src={user.avatar_url} alt={label} />;
  }
  return <span className={`site-user-fallback ${className || ''}`}>{getAvatarInitial(label)}</span>;
};

const useLumiTheme = () => {
  const readTheme = () => document.documentElement.getAttribute('data-theme') || localStorage.getItem('lingbi-theme') || 'light';
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('lingbi-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    const nextTheme = readTheme() === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', nextTheme);
    localStorage.setItem('lingbi-theme', nextTheme);
    setTheme(nextTheme);
  };
  return [theme, toggleTheme] as const;
};

const SiteHeader: React.FC<SitePageProps & { active: 'home' | 'library' | 'account' | 'orders' | 'bookshelf' | 'profile'; currentUser: StoredUser | null; setCurrentUser: (user: StoredUser | null) => void }> = ({
  active,
  currentUser,
  setCurrentUser,
  onHome,
  onLibrary,
  onLogin,
  onRegister,
  onWorkbench,
  onAccount,
  onOrders,
  onBookshelf,
  onProfile,
}) => {
  const [theme, toggleTheme] = useLumiTheme();
  const campaign = useActiveCampaign();

  return (
    <nav className="lumi-top">
      <div className="lumi-container lumi-nav-row">
      <button className="lumi-brand" type="button" onClick={onHome}>
        <span className="lumi-brand-mark"><Sparkles size={18} /></span>
        <span className="lumi-brand-name"><span>灵笔</span> Spirit Brush</span>
      </button>
      <div className="lumi-nav-links" aria-label="主导航">
        <button className={active === 'home' ? 'active' : ''} type="button" onClick={onHome}>首页</button>
        {campaign && <a href="#welfare">{campaign.nav_label || '福利'}</a>}
        <button className={active === 'library' ? 'active' : ''} type="button" onClick={onLibrary}>书阁</button>
        <button type="button" onClick={onWorkbench}>工作台</button>
        <a href="#price">价格</a>
      </div>
      <div className="lumi-nav-right">
        {!currentUser && (
          <span className="lumi-nav-status"><span />服务正常 · 16ms</span>
        )}
        <button className="lumi-theme-toggle" type="button" onClick={toggleTheme} aria-label="切换主题">
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>
        {currentUser ? (
          <UserAccountMenu user={currentUser} setUser={setCurrentUser} onAccount={onAccount} onOrders={onOrders} onBookshelf={onBookshelf} onProfile={onProfile} />
        ) : (
          <button className="lumi-btn lumi-btn-ghost" type="button" onClick={onLogin}>登录</button>
        )}
        <button className="lumi-btn lumi-btn-primary" type="button" onClick={currentUser ? onWorkbench : onRegister}>
          {currentUser ? '开始写书' : '注册账号'}
        </button>
      </div>
      </div>
    </nav>
  );
};

const HomePage: React.FC<SitePageProps> = (props) => {
  const [currentUser, setCurrentUser] = useCurrentUser();
  const [theme, toggleTheme] = useLumiTheme();
  const [welfareBusy, setWelfareBusy] = useState(false);
  const [welfareClaimed, setWelfareClaimed] = useState(false);
  const [welfareMessage, setWelfareMessage] = useState('');
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const campaign = useActiveCampaign();
  const { assets: homeAssets, reload: reloadAssets } = useUserAssets(Boolean(currentUser));
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string; actionLabel?: string; onAction?: () => void; duration?: number } | null>(null);

  const showToast = useCallback((nextToast: { type: 'success' | 'error'; message: string; actionLabel?: string; onAction?: () => void; duration?: number }) => {
    setToast(nextToast);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), toast.duration ?? 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!currentUser) {
      setWelfareClaimed(false);
      return;
    }
    const hasWelfareRecord = homeAssets.records.some((record) => record.source === 'welfare') || homeAssets.orders.some((order) => order.source === 'welfare');
    if (hasWelfareRecord) {
      setWelfareClaimed(true);
      setWelfareMessage('领取完成，订单和余额记录已同步');
    }
  }, [currentUser, homeAssets.records, homeAssets.orders]);

  const claimWelfare = async () => {
    if (!currentUser) {
      showToast({
        type: 'error',
        message: '登录后即可查看并领取活动福利',
        actionLabel: '去登录',
        onAction: props.onLogin,
        duration: 5000,
      });
      return;
    }
    if (welfareBusy || welfareClaimed) return;
    setWelfareBusy(true);
    setWelfareMessage('正在领取福利...');
    try {
      const result = await apiRequest<{ message: string; granted_amount: number; user: StoredUser }>('/users_app/me/welfare/claim', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      localStorage.setItem('lingbi-user', JSON.stringify(result.user));
      setCurrentUser(result.user);
      setWelfareClaimed(true);
      setWelfareMessage(result.granted_amount > 0 ? `已领取 ${result.granted_amount.toLocaleString()} 墨水，可在我的订单查看记录` : '领取完成，订单和余额记录已同步');
      void reloadAssets();
      showToast({
        type: 'success',
        message: '符合资格！墨水领取成功！',
        actionLabel: '去查看>',
        onAction: props.onOrders,
        duration: 5000,
      });
    } catch (error) {
      setWelfareMessage('暂无领取资格');
      showToast({ type: 'error', message: '暂无领取资格', duration: 3000 });
    } finally {
      setWelfareBusy(false);
    }
  };

  const openPayment = (planId: PricingPlan['id']) => {
    if (!currentUser) {
      setLoginPromptOpen(true);
      return;
    }
    void goPayment(planId);
  };

  return (
    <div className="lumi-home-page">
      {toast && (
        <div className={`lumi-toast ${toast.type}`} role="status" aria-live="polite">
          {toast.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{toast.message}</span>
          {toast.actionLabel && toast.onAction ? (
            <button
              type="button"
              onClick={() => {
                setToast(null);
                toast.onAction?.();
              }}
            >
              {toast.actionLabel}
            </button>
          ) : null}
        </div>
      )}
      <nav className="lumi-top">
        <div className="lumi-container lumi-nav-row">
          <button className="lumi-brand" type="button" onClick={props.onHome}>
            <span className="lumi-brand-mark"><Sparkles size={18} /></span>
            <span className="lumi-brand-name"><span>灵笔</span> Spirit Brush</span>
          </button>
          <div className="lumi-nav-links">
            <button type="button" onClick={props.onHome}>首页</button>
            {campaign && <a href="#welfare">{campaign.nav_label || '福利'}</a>}
            <button type="button" onClick={props.onLibrary}>书阁</button>
            <button type="button" onClick={props.onWorkbench}>工作台</button>
            <a href="#price">价格</a>
          </div>
          <div className="lumi-nav-right">
            <span className="lumi-nav-status"><span />服务正常 · 16ms</span>
            <button className="lumi-theme-toggle" type="button" onClick={toggleTheme} aria-label="切换主题">
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {currentUser ? (
              <UserAccountMenu user={currentUser} setUser={setCurrentUser} onAccount={props.onAccount} onOrders={props.onOrders} onBookshelf={props.onBookshelf} onProfile={props.onProfile} />
            ) : (
              <button className="lumi-btn lumi-btn-ghost" type="button" onClick={props.onLogin}>登录</button>
            )}
            <button className="lumi-btn lumi-btn-primary" type="button" onClick={currentUser ? props.onWorkbench : props.onRegister}>
              {currentUser ? '开始写书' : '注册账号'}
            </button>
          </div>
        </div>
      </nav>

      <main>
        {campaign && (
        <section className="lumi-campaign" id="welfare">
          <div className="lumi-container">
            <div className="lumi-camp-banner">
              <div className="lumi-camp-aurora" />
              <div className="lumi-camp-ribbon">{campaign.phase_label}</div>
              <div className="lumi-camp-grid">
                <div>
                  <span className="lumi-pill amber">{campaign.badge}</span>
                  <h2 className="lumi-camp-title">
                    <span>{campaign.headline_prefix}</span>
                    <strong>{campaign.headline_main}</strong>
                  </h2>
                  <div className="lumi-camp-meta">
                    <div><span>原价</span><strong><s>{campaign.original_price}</s> {campaign.current_price}</strong></div>
                    <div><span>{campaign.countdown_label}</span><strong>{campaign.countdown_value}</strong></div>
                    <div><span>中奖率</span><strong>{campaign.win_rate}</strong></div>
                  </div>
                </div>
                <div className="lumi-prize-card">
                  <div className="lumi-prize-head">
                    <span>{campaign.prize_title}</span>
                    <strong>{campaign.prize_price}</strong>
                  </div>
                  <ul>
                    {campaign.features.map((feature) => <li key={feature}><span>✓</span> {feature}</li>)}
                  </ul>
                  <div className="lumi-camp-form">
                    <button type="button" disabled={welfareBusy || welfareClaimed} onClick={claimWelfare}>
                      {welfareClaimed ? '领取完成' : welfareBusy ? '领取中' : '立即领取'}
                    </button>
                  </div>
                  <p><span className="lumi-live-dot" />{campaign.claim_note} · {welfareMessage || '领取结果会同步至余额、流水和订单'}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}

        <section className="lumi-hero">
          <div className="lumi-container">
            <div className="lumi-hero-pitch">
              <span className="lumi-eyebrow"><span>NEW</span>支持 番茄 / 晋江 / 七猫 / 起点 / 知乎盐选 →</span>
              <h1>AI 写小说,<br /><em>躺平拿稿费</em>。</h1>
              <p className="lumi-hero-summary">
                <span>灵笔按真正写网文的工序跑 —— 选平台、定金手指、框定大纲、逐章生成。</span>
                <span>写下一句念头，现在就跑给你看 ↓</span>
              </p>
            </div>

            <div className="lumi-try" id="try">
              <div className="lumi-try-toolbar">
                <div><span>模式</span><button type="button">完整流程 ⌄</button></div>
                <div><span>模型</span><button type="button"><i /> Claude 4.7 ⌄</button></div>
                <div className="lumi-try-toolbar-right">● 队列空闲&nbsp;&nbsp;余额 3 本</div>
              </div>
              <div className="lumi-try-body">
                <div className="lumi-try-input">
                  <label>1. 选 平 台</label>
                  <div className="lumi-platform-row">
                    <button type="button" className="on">番 茄</button>
                    <button type="button">晋 江</button>
                    <button type="button">七 猫</button>
                    <button type="button">起 点</button>
                  </div>
                  <label>2. 大 意 / SEED <span>0 / 200</span></label>
                  <textarea readOnly placeholder="点下方任意一个示例 → 大意自动填入这里" />
                  <label>大 意 库 · 10 个 范 本</label>
                  <div className="lumi-chip-row">
                    {['三点零八分', '重生 98', '反派系统', '离婚惊艳', '鹿群雨', '钢笔少女', '收音机', '婚礼之夜', '隔壁的旧账', '契约停电夜'].map((item) => (
                      <button type="button" key={item}>{item}</button>
                    ))}
                  </div>
                  <label>3. 参 数</label>
                  <div className="lumi-knobs">
                    {['题材 都市悬疑', '基调 冷峻', '章节 30章', '视角 第一人称', '尺度 正常', '节奏 爽文流'].map((item) => {
                      const [name, value] = item.split(' ');
                      return <button type="button" key={item}><span>{name}</span><strong>{value}</strong>⌄</button>;
                    })}
                  </div>
                  <div className="lumi-run-row">
                    <button className="lumi-btn lumi-btn-primary" type="button" onClick={currentUser ? props.onWorkbench : props.onLogin}>跑完 7 步主流程</button>
                    <span>约 8s 演示</span>
                  </div>
                </div>
                <div className="lumi-try-output">
                  <div className="lumi-out-head">
                    <div><span className="on">第一章.md</span><span>大纲.yaml</span><span>人物.md</span></div>
                    <p>耗时 — · 字数 — · 评分 —</p>
                  </div>
                  <div className="lumi-pipe">
                    {['平台', '设定', '15问', '金手指', '大纲', '第1章', '评分'].map((item) => <span key={item}>{item}</span>)}
                  </div>
                  <article className="lumi-result">
                    <h3>三点零八分的钟</h3>
                    <div>第 一 章 · 序 · 钟 停 了</div>
                    <p>那座钟从我祖父的祖父手里就停在三点零八分。</p>
                    <p>家里没人知道为什么 —— 钟身完好，发条上紧后能走两秒，然后又准准地停回去。</p>
                    <p>可那个冬天，我在阁楼整理遗物时，发现了一封封口完好的信。邮戳是民国三十一年。收信人那一栏写着的，是我自己的名字。</p>
                    <div className="lumi-result-actions">
                      <button type="button">继续写第 2 章</button>
                      <button type="button">看完整 30 章大纲</button>
                      <button type="button">下载 .lingbi.md</button>
                    </div>
                  </article>
                </div>
              </div>
            </div>

            <div className="lumi-hero-bullets">
              {['章节数预先规划', '人物设定保持一致', '伏笔三态追踪', '章节质量评分'].map((item) => <span key={item}><b>✓</b>{item}</span>)}
            </div>
          </div>
        </section>

        <section className="lumi-sec" id="workflow">
          <div className="lumi-container">
            <div className="lumi-sec-head">
              <span>创作流程 · 7 步</span>
              <h2>不是聊天框，是一条<span>网文生产线</span>。</h2>
            </div>
            <div className="lumi-flow-grid">
              {[
                ['01', '选平台', '番茄 / 晋江 / 起点 / 七猫模板'],
                ['02', '基础设定', '从一句话灵感扩展世界观'],
                ['03', '15问', 'AI 自动补齐关键问题'],
                ['04', '金手指', '定义能力、代价与边界'],
                ['05', '大纲', '先确定章数和节奏'],
                ['06', '正文', '逐章生成并保持一致'],
                ['07', '评分', '爽点、节奏、伏笔实时建议'],
              ].map(([num, name, desc]) => (
                <div className="lumi-flow-step" key={num}>
                  <div>{num}</div>
                  <strong>{name}</strong>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="lumi-sec">
          <div className="lumi-container">
            <div className="lumi-sec-head">
              <span>POST · 后期工具</span>
              <h2>写完才是<span>开始</span>。</h2>
              <p>伏笔、评分、人物关系、章节重排和平台格式导出，都是小说完成后的刚需。</p>
            </div>
            <div className="lumi-post-grid">
              {[
                ['伏笔追踪', '埋设 / 待回收 / 已回收三态管理', '8 埋 · 5 待 · 3 收'],
                ['章节评分', '网文派 5 维评分和修改建议', 'B+ · 82/100'],
                ['人物关系', '角色关系、矛盾、情感线可视化', '12 人物'],
                ['补章重排', '中途加章后自动重排大纲节奏', '节奏稳定'],
                ['平台导出', '番茄 / 起点 / 晋江格式一键导出', 'Markdown'],
              ].map(([name, desc, stat]) => (
                <div className="lumi-post-card" key={name}>
                  <strong>{name}</strong>
                  <p>{desc}</p>
                  <div><span>{stat}</span></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="lumi-sec" id="price">
          <div className="lumi-container">
            <div className="lumi-sec-head center">
              <span>PRICING · 价格</span>
              <h2>按量购买墨水</h2>
              <p>1 元 = 10 墨水；实际扣费按 AI token 消耗计算，不做包月或包年。</p>
            </div>
            <div className="lumi-prices">
              {pricingPlans.map((plan) => {
                const price = plan.monthlyPrice;
                const sub = plan.subtitle;
                const detail = plan.detail;
                return (
                <div className={`lumi-price ${plan.featured ? 'featured' : ''}`} key={plan.id}>
                  {plan.featured && campaign && <span className="lumi-price-tag">{campaign.headline_prefix}</span>}
                  <h3>{plan.name}</h3>
                  <div className="lumi-price-fee">¥{price}<span>/ 次</span></div>
                  <p>{sub}</p>
                  <p className="lumi-price-detail">{detail}</p>
                  <ul>{plan.features.map((item) => <li key={item}><span>✓</span>{item}</li>)}</ul>
                  <button
                    className={`lumi-btn ${plan.featured ? 'lumi-btn-primary' : 'lumi-btn-ghost'}`}
                    type="button"
                    onClick={() => openPayment(plan.id)}
                  >
                    {plan.cta}
                  </button>
                </div>
              );
              })}
            </div>
          </div>
        </section>

        <section className="lumi-sec">
          <div className="lumi-container">
            <div className="lumi-stats">
              <div><strong>12,847</strong><span>累计完成网文</span><em>↑ 38% / 周</em></div>
              <div><strong>96</strong><span>平均每本章数</span></div>
              <div><strong>38s</strong><span>单章平均生成耗时</span></div>
              <div><strong>82/100</strong><span>用户书均评分</span><em>B+ 段</em></div>
            </div>
          </div>
        </section>

        <section className="lumi-sec" id="faq">
          <div className="lumi-container narrow">
            <div className="lumi-sec-head">
              <span>FAQ · 常见问题</span>
              <h2>还想了解的。</h2>
            </div>
            <div className="lumi-faq">
              {[
                ['章节数为什么要定死？', '定死是为了防止模型无限续写导致整本结构散掉。后续仍可以加章，但大纲会重新整理节奏。'],
                ['15 个反问必须全答吗？', '不需要。跳过的问题 AI 会自动填合理默认，后面也可以回到设定里修改。'],
                ['金手指为什么强调代价？', '没有代价的金手指很容易水。代价能制造限制、反噬和持续张力。'],
                ['生成内容版权归谁？', '归创作者。Plus 及以上支持商用和平台投稿。'],
              ].map(([q, a]) => (
                <details className="lumi-faq-item" key={q}>
                  <summary>{q}<span aria-hidden="true" /></summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="lumi-bottom-cta" id="start">
          <div className="lumi-container">
            <div>
              {campaign && <span>{campaign.phase_label} · {campaign.headline_prefix}</span>}
              <h2>写下你的第一句，看看会变成什么。</h2>
              <p>免费 1 本完整生成 · 不要信用卡 · 不要手机号验证。</p>
              <button className="lumi-btn lumi-btn-primary" type="button" onClick={currentUser ? props.onWorkbench : props.onLogin}>现在开始</button>
            </div>
          </div>
        </section>

        <footer className="lumi-footer">
          <div className="lumi-container">
            <div className="lumi-foot-top">
              <div className="lumi-foot-brand">
                <button className="lumi-brand" type="button" onClick={props.onHome}>
                  <span className="lumi-brand-mark"><Sparkles size={18} /></span>
                  <span className="lumi-brand-name"><span>灵笔</span> Spirit Brush</span>
                </button>
                <p>AI 网文写作工作台。按真正写网文的工序跑 —— 选平台、定金手指、框定大纲、逐章生成、追伏笔、评分。</p>
                <button className="lumi-foot-cta" type="button" onClick={currentUser ? props.onWorkbench : props.onLogin}>
                  现在开始写书
                </button>
              </div>
              <div className="lumi-foot-col">
                <h4>产品</h4>
                <a href="#workflow">7 步主流程</a>
                <a href="#price">定价</a>
                <button type="button" onClick={props.onLibrary}>书阁</button>
                <button type="button" onClick={props.onWorkbench}>工作台</button>
              </div>
              <div className="lumi-foot-col">
                <h4>了解更多</h4>
                {campaign && <a href="#welfare">{campaign.nav_label || '福利'}</a>}
                <a href="#price">价格</a>
                <a href="#try">试一试</a>
                <a href="#faq">常见问题</a>
              </div>
            </div>
            <div className="lumi-foot-bottom">
              <span>© 2026 Spirit Brush · 京 ICP 备 202606010188 号-1</span>
              <span><i />所有服务正常 · 上海 · v1.0.0</span>
            </div>
          </div>
        </footer>
      </main>
      {loginPromptOpen && (
        <div className="account-dialog-backdrop login-required-backdrop" role="presentation" onClick={() => setLoginPromptOpen(false)}>
          <section className="account-dialog login-required-dialog" role="dialog" aria-modal="true" aria-labelledby="login-required-title" onClick={(event) => event.stopPropagation()}>
            <span className="login-required-icon"><LockKeyhole size={30} /></span>
            <h2 id="login-required-title">登录后即可充值</h2>
            <p>登录后可购买墨水，并在账户中查看余额和消费记录。</p>
            <button className="lumi-btn lumi-btn-primary" type="button" onClick={props.onLogin}>去登录</button>
            <button className="lumi-btn lumi-btn-ghost" type="button" onClick={() => setLoginPromptOpen(false)}>暂不登录</button>
          </section>
        </div>
      )}
    </div>
  );
};

interface LibraryAuthorItem {
  id: string;
  name: string;
  level: '殿堂作家' | '金番作家';
  work: string;
  avatar_url: string;
  sort: number;
}

const useLibraryAuthors = () => {
  const [authors, setAuthors] = useState<LibraryAuthorItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    const loadAuthors = async () => {
      try {
        const result = await apiRequest<LibraryAuthorItem[]>('/novel_project_app/library/authors');
        if (!cancelled) setAuthors(result.filter((item) => item.avatar_url).slice(0, 7));
      } catch {
        if (!cancelled) setAuthors([]);
      }
    };
    void loadAuthors();
    return () => {
      cancelled = true;
    };
  }, []);

  return authors;
};

type LibrarySectionKey = 'male_featured' | 'female_featured' | 'copyright' | 'activity_wall';

interface LibrarySectionItem {
  id: string;
  section: LibrarySectionKey;
  sort: number;
  book: ShelfBook;
}

const useLibrarySections = () => {
  const [items, setItems] = useState<LibrarySectionItem[]>([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await apiRequest<LibrarySectionItem[]>('/novel_project_app/library/sections');
        if (!cancelled) setItems(result);
      } catch {
        if (!cancelled) setItems([]);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);
  return (section: LibrarySectionKey) => items
    .filter((item) => item.section === section)
    .sort((a, b) => a.sort - b.sort)
    .map((item) => item.book);
};

const useCategoryRankingBooks = (channel: LibraryRankingChannel, category: string) => {
  const [popular, setPopular] = useState<ShelfBook[]>([]);
  const [latest, setLatest] = useState<ShelfBook[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!category) {
      setPopular([]);
      setLatest([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const reader = channel === '女频' ? '女生' : '男生';
      const params = new URLSearchParams({ reader, category });
      try {
        const [popularRows, latestRows] = await Promise.all([
          apiRequest<ShelfBook[]>(`/novel_project_app/library/books?${params.toString()}&sort=hot`),
          apiRequest<ShelfBook[]>(`/novel_project_app/library/books?${params.toString()}&sort=latest`),
        ]);
        if (!cancelled) {
          setPopular(popularRows.slice(0, 8));
          setLatest(latestRows.slice(0, 8));
        }
      } catch {
        if (!cancelled) {
          setPopular([]);
          setLatest([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [category, channel]);

  return { popular, latest, loading };
};

const bookstoreGenres = ['西方奇幻', '东方仙侠', '科幻末世', '都市日常', '都市修真', '都市高武', '历史古代', '战神赘婿', '传统玄幻', '悬疑脑洞'];

const BookstoreCover: React.FC<{ book: ShelfBook; className?: string }> = ({ book, className }) => (
  <button className={`fqn-cover ${className || ''}`} type="button" onClick={() => goBookDetail(book.slug)}>
    <img src={book.cover} alt={book.title} />
    <span><strong>{book.title}</strong><small>{book.author}</small></span>
  </button>
);

const cloudPositions = [
  [0.08, 0.08], [0.27, 0.18], [0.48, 0.06], [0.69, 0.17], [0.9, 0.07],
  [0.04, 0.48], [0.25, 0.56], [0.49, 0.42], [0.72, 0.55], [0.94, 0.43],
  [0.11, 0.82], [0.34, 0.78], [0.58, 0.8], [0.81, 0.83], [0.96, 0.72],
] as const;

const cloudParticleSizes = [7, 11, 6, 14, 8, 10, 5, 13, 7, 9, 15, 6, 11, 8, 5, 12, 7, 10];

const BookstorePhysicsCloud: React.FC<{ books: ShelfBook[] }> = ({ books }) => {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const particleRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const bodiesRef = useRef<MatterBody[]>([]);
  const dragRef = useRef<{
    body: MatterBody;
    index: number;
    offsetX: number;
    offsetY: number;
    lastX: number;
    lastY: number;
    lastAt: number;
    velocityX: number;
    velocityY: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);
  const pointerRef = useRef({ x: 0, y: 0, active: false });
  const reducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || reducedMotion) return undefined;

    const engine = Engine.create();
    engine.gravity.x = 0;
    engine.gravity.y = 0;
    let animationFrame = 0;
    let lastFrame = performance.now();
    let resizeTimer = 0;
    let particleBodies: MatterBody[] = [];

    const syncElements = () => {
      bodiesRef.current.forEach((body, index) => {
        const element = cardRefs.current[index];
        if (!element) return;
        element.style.transform = `translate3d(${body.position.x - element.offsetWidth / 2}px, ${body.position.y - element.offsetHeight / 2}px, 0) rotate(${body.angle}rad)`;
      });
      particleBodies.forEach((body, index) => {
        const element = particleRefs.current[index];
        if (!element) return;
        element.style.transform = `translate3d(${body.position.x - element.offsetWidth / 2}px, ${body.position.y - element.offsetHeight / 2}px, 0)`;
      });
    };

    const buildWorld = () => {
      const { width, height } = stage.getBoundingClientRect();
      if (!width || !height) return;

      Composite.clear(engine.world, false, true);
      const bottom = height - 92;
      const wallSize = 140;
      const walls = [
        Bodies.rectangle(width / 2, -wallSize / 2, width + wallSize * 2, wallSize, { isStatic: true }),
        Bodies.rectangle(width / 2, bottom + wallSize / 2, width + wallSize * 2, wallSize, { isStatic: true }),
        Bodies.rectangle(-wallSize / 2, bottom / 2, wallSize, bottom + wallSize * 2, { isStatic: true }),
        Bodies.rectangle(width + wallSize / 2, bottom / 2, wallSize, bottom + wallSize * 2, { isStatic: true }),
        Bodies.rectangle(width / 2, bottom + 8, 190, 74, {
          isStatic: true,
          chamfer: { radius: 36 },
        }),
      ];

      const cardBodies = books.map((_, index) => {
        const element = cardRefs.current[index];
        const cardWidth = element?.offsetWidth || 190;
        const cardHeight = element?.offsetHeight || Math.round(cardWidth * 4 / 3);
        const [positionX, positionY] = cloudPositions[index % cloudPositions.length];
        const x = Math.min(width - cardWidth / 2, Math.max(cardWidth / 2, width * positionX));
        const y = Math.min(bottom - cardHeight / 2, Math.max(cardHeight / 2, bottom * positionY));
        const body = Bodies.rectangle(x, y, cardWidth, cardHeight, {
          restitution: 0.88,
          friction: 0.04,
          frictionStatic: 0.06,
          frictionAir: 0.0015,
          density: 0.0012,
          chamfer: { radius: 7 },
        });
        Body.setAngle(body, ((index % 5) - 2) * 0.025);
        Body.setVelocity(body, {
          x: ((index * 17) % 9 - 4) * 0.18,
          y: ((index * 13) % 7 - 3) * 0.14,
        });
        Body.setAngularVelocity(body, ((index % 4) - 1.5) * 0.0008);
        return body;
      });

      particleBodies = cloudParticleSizes.map((radius, index) => {
        const x = 28 + ((index * 113) % Math.max(40, width - 56));
        const y = 26 + ((index * 79) % Math.max(40, bottom - 52));
        const body = Bodies.circle(x, y, radius, {
          restitution: 0.92,
          friction: 0.03,
          frictionAir: 0.004,
          density: 0.0006,
        });
        Body.setVelocity(body, {
          x: ((index % 5) - 2) * 0.26,
          y: (((index + 2) % 5) - 2) * 0.22,
        });
        return body;
      });

      bodiesRef.current = cardBodies;
      Composite.add(engine.world, [...walls, ...cardBodies, ...particleBodies]);
      syncElements();
    };

    const flowVector = (now: number, index: number) => {
      const phase = index * 1.73;
      return {
        phase,
        x: Math.cos(now / 3400 + phase) + Math.sin(now / 5100 + phase * 0.42) * 0.44,
        y: Math.sin(now / 3900 + phase * 0.78) + Math.cos(now / 4700 + phase) * 0.4,
      };
    };

    const animate = (now: number) => {
      const delta = Math.min(1000 / 60, now - lastFrame || 1000 / 60);
      lastFrame = now;
      const { width, height } = stage.getBoundingClientRect();
      const centerY = (height - 92) / 2;
      const compact = width < 720;
      const minSpeed = compact ? 0.58 : 0.82;
      const maxSpeed = compact ? 1.75 : 2.35;

      bodiesRef.current.forEach((body, index) => {
        if (body.isStatic) return;
        const flow = flowVector(now, index);
        const drift = 0.0000032 * body.mass;
        const edgeForce = 0.000008 * body.mass;
        const edgeX = body.position.x < width * 0.13 ? 1 : body.position.x > width * 0.87 ? -1 : 0;
        const edgeY = body.position.y < centerY * 0.2 ? 1 : body.position.y > centerY * 1.8 ? -1 : 0;
        Body.applyForce(body, body.position, {
          x: flow.x * drift + edgeX * edgeForce,
          y: flow.y * drift + edgeY * edgeForce,
        });

        if (pointerRef.current.active) {
          const pointerX = body.position.x - pointerRef.current.x;
          const pointerY = body.position.y - pointerRef.current.y;
          const pointerDistance = Math.max(40, Math.hypot(pointerX, pointerY));
          if (pointerDistance < 300) {
            const pointerForce = (1 - pointerDistance / 300) * 0.000024 * body.mass;
            Body.applyForce(body, body.position, {
              x: (pointerX / pointerDistance) * pointerForce,
              y: (pointerY / pointerDistance) * pointerForce,
            });
          }
        }

      });

      // 卡片接近时提前推开，避免长期碰撞后挤成静止的一团。
      for (let index = 0; index < bodiesRef.current.length; index += 1) {
        const body = bodiesRef.current[index];
        if (body.isStatic) continue;
        for (let nextIndex = index + 1; nextIndex < bodiesRef.current.length; nextIndex += 1) {
          const other = bodiesRef.current[nextIndex];
          if (other.isStatic) continue;
          const distanceX = body.position.x - other.position.x;
          const distanceY = body.position.y - other.position.y;
          const distance = Math.max(48, Math.hypot(distanceX, distanceY));
          const separation = compact ? 205 : 255;
          if (distance >= separation) continue;
          const strength = (1 - distance / separation) * 0.000012;
          Body.applyForce(body, body.position, {
            x: (distanceX / distance) * strength * body.mass,
            y: (distanceY / distance) * strength * body.mass,
          });
          Body.applyForce(other, other.position, {
            x: -(distanceX / distance) * strength * other.mass,
            y: -(distanceY / distance) * strength * other.mass,
          });
        }
      }

      particleBodies.forEach((body, index) => {
        const drift = 0.000002 * body.mass;
        Body.applyForce(body, body.position, {
          x: Math.sin(now / 1700 + index) * drift,
          y: Math.cos(now / 2100 + index * 0.7) * drift,
        });
      });

      Engine.update(engine, delta);
      bodiesRef.current.forEach((body, index) => {
        if (body.isStatic) return;
        const flow = flowVector(now, index);
        const flowLength = Math.max(0.2, Math.hypot(flow.x, flow.y));
        if (body.speed < minSpeed) {
          const currentWeight = body.speed > 0.08 ? 0.62 : 0;
          const directionX = (body.velocity.x / Math.max(body.speed, 0.08)) * currentWeight
            + (flow.x / flowLength) * (1 - currentWeight);
          const directionY = (body.velocity.y / Math.max(body.speed, 0.08)) * currentWeight
            + (flow.y / flowLength) * (1 - currentWeight);
          const directionLength = Math.max(0.2, Math.hypot(directionX, directionY));
          Body.setVelocity(body, {
            x: (directionX / directionLength) * minSpeed,
            y: (directionY / directionLength) * minSpeed,
          });
        } else if (body.speed > maxSpeed) {
          const scale = maxSpeed / body.speed;
          Body.setVelocity(body, {
            x: body.velocity.x * scale,
            y: body.velocity.y * scale,
          });
        }
        const targetSpin = Math.sin(now / 4300 + flow.phase) * 0.0024;
        Body.setAngularVelocity(body, body.angularVelocity * 0.985 + targetSpin * 0.015);
      });
      syncElements();
      animationFrame = window.requestAnimationFrame(animate);
    };

    const pointerPosition = (event: PointerEvent) => {
      const rect = stage.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      const card = target?.closest<HTMLButtonElement>('[data-physics-index]');
      if (!card) return;
      const index = Number(card.dataset.physicsIndex);
      const body = bodiesRef.current[index];
      if (!body) return;
      const pointer = pointerPosition(event);
      card.setPointerCapture(event.pointerId);
      Body.setStatic(body, true);
      dragRef.current = {
        body,
        index,
        offsetX: pointer.x - body.position.x,
        offsetY: pointer.y - body.position.y,
        lastX: pointer.x,
        lastY: pointer.y,
        lastAt: performance.now(),
        velocityX: 0,
        velocityY: 0,
        moved: false,
      };
      card.classList.add('is-dragging');
    };

    const handlePointerMove = (event: PointerEvent) => {
      const hoverPointer = pointerPosition(event);
      pointerRef.current = { ...hoverPointer, active: true };
      const drag = dragRef.current;
      if (!drag) return;
      const pointer = hoverPointer;
      const now = performance.now();
      const elapsed = Math.max(8, now - drag.lastAt);
      const distance = Math.hypot(pointer.x - drag.lastX, pointer.y - drag.lastY);
      if (distance > 2) drag.moved = true;
      drag.velocityX = ((pointer.x - drag.lastX) / elapsed) * 16.67;
      drag.velocityY = ((pointer.y - drag.lastY) / elapsed) * 16.67;
      drag.lastX = pointer.x;
      drag.lastY = pointer.y;
      drag.lastAt = now;
      Body.setPosition(drag.body, {
        x: pointer.x - drag.offsetX,
        y: pointer.y - drag.offsetY,
      });
      if (drag.moved) event.preventDefault();
    };

    const handlePointerEnter = (event: PointerEvent) => {
      pointerRef.current = { ...pointerPosition(event), active: true };
    };

    const handlePointerLeave = () => {
      pointerRef.current.active = false;
    };

    const releasePointer = () => {
      const drag = dragRef.current;
      if (!drag) return;
      cardRefs.current[drag.index]?.classList.remove('is-dragging');
      Body.setStatic(drag.body, false);
      Body.setVelocity(drag.body, {
        x: Math.max(-9, Math.min(9, drag.velocityX)),
        y: Math.max(-9, Math.min(9, drag.velocityY)),
      });
      Body.setAngularVelocity(drag.body, Math.max(-0.08, Math.min(0.08, drag.velocityX * 0.008)));
      suppressClickRef.current = drag.moved;
      dragRef.current = null;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    };

    buildWorld();
    animationFrame = window.requestAnimationFrame(animate);
    const resizeObserver = new ResizeObserver(() => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(buildWorld, 140);
    });
    resizeObserver.observe(stage);
    stage.addEventListener('pointerdown', handlePointerDown);
    stage.addEventListener('pointerenter', handlePointerEnter);
    stage.addEventListener('pointermove', handlePointerMove);
    stage.addEventListener('pointerleave', handlePointerLeave);
    stage.addEventListener('pointerup', releasePointer);
    stage.addEventListener('pointercancel', releasePointer);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(resizeTimer);
      resizeObserver.disconnect();
      stage.removeEventListener('pointerdown', handlePointerDown);
      stage.removeEventListener('pointerenter', handlePointerEnter);
      stage.removeEventListener('pointermove', handlePointerMove);
      stage.removeEventListener('pointerleave', handlePointerLeave);
      stage.removeEventListener('pointerup', releasePointer);
      stage.removeEventListener('pointercancel', releasePointer);
      Engine.clear(engine);
      bodiesRef.current = [];
    };
  }, [books, reducedMotion]);

  return (
    <section className="fqn-cover-cloud" aria-label="动态作品墙">
      <div ref={stageRef} className={`fqn-cloud-stage ${reducedMotion ? 'is-static' : 'is-physics'}`}>
        <div className="fqn-cloud-particles" aria-hidden="true">
          {cloudParticleSizes.map((size, index) => (
            <span
              className={`particle-tone-${index % 3}`}
              key={`cloud-particle-${index}`}
              ref={(element) => {
                particleRefs.current[index] = element;
              }}
              style={{ width: size * 2, height: size * 2 }}
            />
          ))}
        </div>
        {books.map((book, index) => (
          <button
            className={`fqn-physics-card size-${index % 3}`}
            data-physics-index={index}
            type="button"
            key={`${book.slug}-cloud-${index}`}
            ref={(element) => {
              cardRefs.current[index] = element;
            }}
            onClick={() => {
              if (suppressClickRef.current) return;
              goBookDetail(book.slug);
            }}
          >
            <img src={book.cover} alt={book.title} draggable={false} />
          </button>
        ))}
      </div>
    </section>
  );
};

export const BookstorePage: React.FC<SitePageProps> = (props) => {
  const [currentUser, setCurrentUser] = useCurrentUser();
  const [rankingTab, setRankingTab] = useState<'男频排行榜' | '女频排行榜'>('男频排行榜');
  const [genre, setGenre] = useState('西方奇幻');
  const [peakPage, setPeakPage] = useState(0);
  const peakTouchStart = useRef<number | null>(null);
  const [notice, setNotice] = useState<{ title: string; content: string } | null>(null);
  const rankingChannel: LibraryRankingChannel = rankingTab === '男频排行榜' ? '男频' : '女频';
  const rankingItems = useLibraryRankings(rankingChannel);
  const newsItems = useSiteNews();
  const updateItems = useLibraryUpdates();
  const categoryItems = useLibraryCategories();
  const managedAuthors = useLibraryAuthors();
  const sectionBooks = useLibrarySections();
  const maleFeaturedBooks = sectionBooks('male_featured');
  const femaleFeaturedBooks = sectionBooks('female_featured');
  const copyrightBooks = sectionBooks('copyright');
  const activityWallBooks = sectionBooks('activity_wall');
  const visibleGenres = categoryItems.filter((item) => item.channel === rankingChannel).map((item) => item.name);
  const categoryRankingBooks = useCategoryRankingBooks(rankingChannel, genre);
  const peakPageCount = Math.max(1, Math.min(3, Math.ceil(rankingItems.length / 9)));
  const peakBooks = useMemo(
    () => rankingItems.slice(peakPage * 9, peakPage * 9 + 9),
    [peakPage, rankingItems],
  );
  const scrollToSection = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  useEffect(() => {
    if (visibleGenres.length && !visibleGenres.includes(genre)) setGenre(visibleGenres[0]);
  }, [genre, visibleGenres]);
  useEffect(() => {
    setPeakPage(0);
  }, [rankingChannel]);
  useEffect(() => {
    if (peakPage >= peakPageCount) setPeakPage(Math.max(0, peakPageCount - 1));
  }, [peakPage, peakPageCount]);

  return (
    <div className="fqn-page lumi-shell-page">
      <SiteHeader
        {...props}
        active="library"
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
      />

      <main>
        <section className="fqn-hero">
          <img src="/assets/bookstore/lingbi-library-banner.jpg" alt="" loading="eager" decoding="async" fetchPriority="high" />
          <div className="fqn-shell fqn-hero-copy">
            <span>《灵笔原创》</span>
            <h1>2026 年度<br /><strong>创作扶持计划</strong></h1>
            <p>全新升级四大激励，优质原创持续扶持</p>
          </div>
          <i />
        </section>

        <section className="fqn-shortcuts">
          <div className="fqn-shell">
            {[
              ['创作福利', '灵笔作者福利区'],
              ['作者专区', '创建作品、查看作品数据及收益'],
              ['版权专区', '优秀版权作品展示'],
            ].map(([title, text], index) => (
              <button
                type="button"
                key={title}
                onClick={() => {
                  if (index === 0) setNotice({ title, content: '作者福利、创作扶持和活动信息将由运营后台统一发布，当前可先查看最新资讯。' });
                  if (index === 1) scrollToSection('fqn-authors');
                  if (index === 2) scrollToSection('fqn-copyright');
                }}
              >
                <span><strong>{title}</strong><small>{text}</small></span><ChevronRight size={20} />
              </button>
            ))}
          </div>
        </section>

        <section className="fqn-block fqn-peak-news" id="fqn-peak-news">
          <div className="fqn-shell">
            <div className="fqn-peak">
              <div className="fqn-title"><h2>灵笔巅峰榜</h2><p>根据作品好评、人气、互动等综合得分排行</p></div>
              <div
                className="fqn-peak-grid"
                key={`${rankingChannel}-${peakPage}`}
                onTouchStart={(event) => { peakTouchStart.current = event.touches[0]?.clientX ?? null; }}
                onTouchEnd={(event) => {
                  if (peakTouchStart.current === null || peakPageCount <= 1) return;
                  const distance = (event.changedTouches[0]?.clientX ?? peakTouchStart.current) - peakTouchStart.current;
                  if (Math.abs(distance) >= 42) {
                    setPeakPage((current) => distance < 0
                      ? Math.min(peakPageCount - 1, current + 1)
                      : Math.max(0, current - 1));
                  }
                  peakTouchStart.current = null;
                }}
              >
                {peakBooks.map((book, index) => (
                  <button type="button" key={book.slug} onClick={() => goBookDetail(book.slug)}>
                    <em>{book.rank || peakPage * 9 + index + 1}</em>
                    <span><strong>{book.title}</strong><small>{book.category || book.status}</small></span>
                    <img
                      src={book.cover || book.cover_url || bookCover(book.slug)}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
              {peakPageCount > 1 && (
                <div className="fqn-dots" aria-label="巅峰榜分页">
                  {Array.from({ length: peakPageCount }, (_, page) => (
                    <button
                      type="button"
                      className={page === peakPage ? 'active' : ''}
                      aria-label={`第 ${page + 1} 页`}
                      aria-current={page === peakPage ? 'page' : undefined}
                      key={page}
                      onClick={() => setPeakPage(page)}
                    />
                  ))}
                </div>
              )}
            </div>
            <aside className="fqn-news">
              <div className="fqn-title"><h2>最新资讯</h2></div>
              {newsItems.slice(0, 9).map((item) => (
                <button type="button" key={item.id || item.title} onClick={() => setNotice({ title: item.title, content: item.content || item.desc })}>
                  <span>{item.title}</span><ChevronRight size={17} />
                </button>
              ))}
            </aside>
          </div>
        </section>

        <section className="fqn-block fqn-authors-section" id="fqn-authors">
          <div className="fqn-shell">
            <div className="fqn-title fqn-title-row"><h2>殿堂、金番作家</h2><button type="button" onClick={props.onWorkbench}>成为作家 <ChevronRight size={14} /></button></div>
            <div className="fqn-authors">
              {managedAuthors.map((author) => (
                <button type="button" key={author.id} onClick={() => setNotice({ title: author.name, content: `${author.level}，代表方向：${author.work}。` })}>
                  <img src={author.avatar_url} alt={author.name} />
                  <strong>{author.name}</strong>
                  <span>{author.level}</span>
                  <small>{author.work}</small>
                </button>
              ))}
              {!managedAuthors.length && <div className="fqn-managed-empty">暂无启用作家，请在后台“作家管理”中配置。</div>}
            </div>
          </div>
        </section>

        <section className="fqn-gender">
          <article className="male">
            <div className="fqn-gender-inner">
              <h2>男频精选</h2>
              <div>{maleFeaturedBooks.map((book) => <BookstoreCover book={book} key={book.slug} />)}</div>
            </div>
          </article>
          <article className="female">
            <div className="fqn-gender-inner">
              <h2>女频精选</h2>
              <div>{femaleFeaturedBooks.map((book) => <BookstoreCover book={book} key={book.slug} />)}</div>
            </div>
          </article>
        </section>

        <section className="fqn-block fqn-rankings">
          <div className="fqn-shell">
            <div className="fqn-ranking-tabs">
              {(['男频排行榜', '女频排行榜'] as const).map((tab) => (
                <button className={rankingTab === tab ? 'active' : ''} type="button" key={tab} onClick={() => setRankingTab(tab)}>{tab}</button>
              ))}
            </div>
            <div className="fqn-genre-tabs">
              {(visibleGenres.length ? visibleGenres : bookstoreGenres).map((item) => <button className={genre === item ? 'active' : ''} type="button" key={item} onClick={() => setGenre(item)}>{item}</button>)}
            </div>
            <div className="fqn-ranking-panels">
              {[
                { title: `${genre}·阅读榜`, books: categoryRankingBooks.popular, tone: 'mint' },
                { title: `${genre}·新书榜`, books: categoryRankingBooks.latest, tone: 'peach' },
              ].map((panel) => (
                <article className={panel.tone} key={panel.title}>
                  <h3>{panel.title} <ChevronRight size={16} /></h3>
                  <small>仅展示原创作品，统计时间截至今日 24:00</small>
                  {categoryRankingBooks.loading && <div className="fqn-ranking-empty">正在读取分类作品...</div>}
                  {!categoryRankingBooks.loading && !panel.books.length && <div className="fqn-ranking-empty">该分类暂时没有已发布作品</div>}
                  <ol>
                    {panel.books.map((book, index) => (
                      <li className={index === 0 ? 'featured' : ''} key={`${panel.title}-${book.slug}`}>
                        <em>{String(index + 1).padStart(2, '0')}</em>
                        <button type="button" onClick={() => goBookDetail(book.slug)}>
                          <strong>{book.title}</strong><small>{book.author}</small>
                          {index === 0 && <p>{book.desc}</p>}
                        </button>
                        {index === 0 ? <img src={book.cover} alt="" /> : <span>-</span>}
                      </li>
                    ))}
                  </ol>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="fqn-copyright" id="fqn-copyright">
          <div className="fqn-copyright-copy">
            <h2>版权改编</h2>
            <p>热门版权改编动漫、影视尽在灵笔书阁，一起读好故事，追精品原创小说</p>
          </div>
          <div className="fqn-copyright-wall">
            {copyrightBooks.map((book, index) => (
              <button type="button" key={`${book.slug}-${index}`} onClick={() => goBookDetail(book.slug)}><img src={book.cover} alt={book.title} /></button>
            ))}
          </div>
        </section>

        <BookstorePhysicsCloud books={activityWallBooks} />

        <section className="fqn-block fqn-updates">
          <div className="fqn-shell">
            <div className="fqn-title centered"><h2>最近更新</h2></div>
            <div className="fqn-update-card">
              <div className="fqn-update-head"><span>类型</span><span>书名</span><span>最新章节</span><span>作者</span><span>更新时间</span></div>
              <div className="fqn-update-list">
                {updateItems.map((item) => (
                  <button type="button" key={item.id} onClick={() => item.slug && goBookDetail(item.slug)}>
                    <span>{item.type}</span><strong>{item.title}</strong><span>{item.chapter}</span><span>{item.author}</span><time>{item.updatedAt}</time>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="fqn-footer">
        <div className="fqn-shell">
          <div>
            <button className="fqn-brand" type="button" onClick={props.onHome}><span><Sparkles size={18} /></span><strong>灵笔书阁</strong></button>
            <button className="fqn-footer-link" type="button" onClick={() => setNotice({ title: '灵笔书阁用户协议', content: '使用灵笔书阁即表示您同意遵守平台内容规范、版权规则与账号使用约定。作品版权归创作者或合法权利人所有。' })}>《灵笔书阁用户协议》</button>
            <button className="fqn-footer-link" type="button" onClick={() => setNotice({ title: '灵笔书阁隐私政策', content: '平台仅在提供账号、阅读与创作服务所必需的范围内处理用户信息，并采取合理措施保护数据安全。' })}>《灵笔书阁隐私政策》</button>
          </div>
          <div><p>原创故事阅读与创作平台</p><p>作品内容由创作者独立发布</p><p>联系邮箱：service@spiritbrush.cn</p></div>
          <div><strong>让每一个好故事，都找到它的读者。</strong><small>© 2026 Spirit Brush</small></div>
        </div>
      </footer>

      {notice && (
        <div className="fqn-notice-backdrop" role="presentation" onMouseDown={() => setNotice(null)}>
          <section className="fqn-notice-dialog" role="dialog" aria-modal="true" aria-labelledby="fqn-notice-title" onMouseDown={(event) => event.stopPropagation()}>
            <button className="fqn-notice-close" type="button" aria-label="关闭" onClick={() => setNotice(null)}><X size={19} /></button>
            <span>SPIRIT BRUSH</span>
            <h2 id="fqn-notice-title">{notice.title}</h2>
            <p>{notice.content}</p>
          </section>
        </div>
      )}
    </div>
  );
};

export const LibraryPage: React.FC<SitePageProps> = (props) => {
  const [currentUser, setCurrentUser] = useCurrentUser();
  const [page, setPage] = useState(1);
  const [peakExpanded, setPeakExpanded] = useState(false);
  const [rankingChannel, setRankingChannel] = useState<LibraryRankingChannel>('男频');
  const [filters, setFilters] = useState<LibraryFilters>({ reader: '全部', category: '全部', status: '全部', sort: 'hot', keyword: '' });
  const [searchDraft, setSearchDraft] = useState('');
  const { books, loading, error } = useLibraryBooks(filters);
  const rankings = useLibraryRankings(rankingChannel);
  const featureBooks = useLibraryFeatures();
  const newsItems = useSiteNews();
  const updateItems = useLibraryUpdates();
  const categoryItems = useLibraryCategories();
  const filterCategories = Array.from(new Set(categoryItems.map((item) => item.name)));
  const pageSize = 12;
  const pageCount = Math.max(1, Math.ceil(books.length / pageSize));
  const pageBooks = useMemo(() => books.slice((page - 1) * pageSize, page * pageSize), [books, page]);
  const visibleRankings = useMemo(() => rankings.slice(0, peakExpanded ? 10 : 5), [peakExpanded, rankings]);
  useEffect(() => {
    setPage((value) => Math.min(value, pageCount));
  }, [pageCount]);

  const updateFilter = (key: 'reader' | 'category' | 'status', value: string) => {
    setPage(1);
    setFilters((items) => ({ ...items, [key]: value }));
  };

  const updateSort = (sort: LibraryFilters['sort']) => {
    setPage(1);
    setFilters((items) => ({ ...items, sort }));
  };

  const submitSearch = () => {
    setPage(1);
    setFilters((items) => ({ ...items, keyword: searchDraft.trim() }));
  };

  const openRankBook = (item?: LibraryRankingItem) => {
    if (!item) return;
    const slug = item.slug || books.find((book) => book.title === item.title)?.slug;
    if (slug) goBookDetail(slug);
  };

  return (
    <div className="site-page library-page lumi-shell-page">
      <SiteHeader {...props} active="library" currentUser={currentUser} setCurrentUser={setCurrentUser} />
      <main className="library-main">
        <section className="library-peak-layout" aria-label="书阁巅峰榜与站内动态">
          <article className="library-peak-panel">
            <div className="library-peak-head">
              <div>
                <span>PEAK RANKING</span>
                <h1>巅峰榜</h1>
                <p>综合阅读、收藏、追更和完读表现，优先展示今天最值得打开的小说。</p>
              </div>
              <div className="library-peak-badge" aria-label="今日热度">
                <em>今日热度</em>
                <strong>TOP 10</strong>
              </div>
            </div>
            <div className="library-rank-tabs">
              {libraryRankingChannels.map((item) => (
                <button
                  className={rankingChannel === item ? 'active' : ''}
                  type="button"
                  key={item}
                  onClick={() => {
                    setRankingChannel(item);
                    setPeakExpanded(false);
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="library-peak-list">
              {visibleRankings.map((item, index) => (
                <button type="button" key={item.id || `${item.title}-${index}`} onClick={() => openRankBook(item)}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <strong>{item.title}</strong>
                  <em>{item.status} · {item.update}</em>
                </button>
              ))}
              {!visibleRankings.length && <div className="library-rank-empty">当前分类暂无榜单作品，请在后台巅峰榜管理中配置。</div>}
            </div>
            {rankings.length > 5 && (
              <div className="library-peak-more-row">
                <button type="button" onClick={() => setPeakExpanded((value) => !value)}>
                  {peakExpanded ? '收起榜单' : '显示更多'}
                </button>
              </div>
            )}
            <div className="library-peak-actions">
              <button type="button" onClick={() => openRankBook(rankings[0])}>从榜首开始读</button>
            </div>
          </article>

          <aside className="library-news-panel" aria-label="站内动态">
            <div className="library-news-head">
              <span>NEWS</span>
              <h2>站内动态</h2>
            </div>
            <div className="library-news-list">
              {newsItems.map((item) => (
                <article className="library-news-row" key={item.id || item.title}>
                  <time>{item.date}</time>
                  <div>
                    <strong>{item.title}</strong>
                    <p>{item.desc}</p>
                  </div>
                </article>
              ))}
            </div>
          </aside>
        </section>

        {featureBooks.length > 0 && <section className="library-feature-panel" aria-label="今日推荐">
          <div className="library-module-head">
            <span>今日推荐</span>
            <h2>今天最值得打开的小说</h2>
          </div>
          <div className="library-cover-viewport">
            <div className="library-cover-track">
              {featureBooks.map((book, index) => (
                <button className="library-cover-card" type="button" key={book.id || `${book.title}-${index}`} onClick={() => book.slug && goBookDetail(book.slug)}>
                  <img
                    src={book.cover || bookCover(`feature-${book.slug || book.title}`)}
                    alt={book.title}
                    onError={(event) => {
                      const fallback = bookCover(`feature-${book.slug || book.title || index}`);
                      if (event.currentTarget.src !== fallback) {
                        event.currentTarget.src = fallback;
                      }
                    }}
                  />
                  <span className="library-cover-shade" />
                  <em>{book.label}</em>
                  <strong>{book.title}</strong>
                  <small>{book.author || '灵笔书阁'} · {book.status || '连载中'}</small>
                </button>
              ))}
            </div>
          </div>
        </section>}

        <section className="library-filter-panel" aria-label="书阁筛选">
          {[
            ['reader', '读者', ['全部', '男生', '女生']],
            ['category', '分类', ['全部', ...(filterCategories.length ? filterCategories : ['主题', '角色', '情节'])]],
            ['status', '状态', ['全部', '已完结', '连载中']],
          ].map(([key, label, options]) => (
            <div className="library-filter-row" key={label as string}>
              <span>{label as string}：</span>
              {(options as string[]).map((item) => (
                <button
                  className={filters[key as 'reader' | 'category' | 'status'] === item ? 'active' : ''}
                  type="button"
                  key={item}
                  onClick={() => updateFilter(key as 'reader' | 'category' | 'status', item)}
                >
                  {item}
                </button>
              ))}
            </div>
          ))}
        </section>

        <section className="library-list-panel" id="library-catalog">
          <div className="library-list-head">
            <div className="library-list-tabs">
              <button className={filters.sort === 'hot' ? 'active' : ''} type="button" onClick={() => updateSort('hot')}>最热</button>
              <button className={filters.sort === 'latest' ? 'active' : ''} type="button" onClick={() => updateSort('latest')}>最新</button>
            </div>
            {loading || error ? <span className="library-sync-state">{loading ? '同步书阁中...' : error}</span> : null}
            <label className="library-search slim">
              <Search size={16} />
              <input
                value={searchDraft}
                placeholder="搜索书名或作者"
                onChange={(event) => setSearchDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    submitSearch();
                  }
                }}
              />
            </label>
          </div>

          <div className="library-book-list">
            {pageBooks.map((book) => (
              <button className="library-book-item" type="button" key={book.title} onClick={() => goBookDetail(book.slug)}>
                <img src={book.cover} alt={book.title} />
                <div>
                  <h3>{book.title}</h3>
                  <p>作者：{book.author}</p>
                  <p>{book.status} · {book.words}</p>
                  <p className="book-desc">{book.desc}</p>
                  <p>{book.update}</p>
                </div>
              </button>
            ))}
            {!pageBooks.length && (
              <div className="library-empty-state">
                没有找到符合条件的作品，换个筛选或搜索词试试。
              </div>
            )}
          </div>

          <div className="shelf-pagination light">
            <button type="button" disabled={page === 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
              <ChevronLeft size={16} />
              上一页
            </button>
            {Array.from({ length: pageCount }, (_, index) => index + 1).map((item) => (
              <button className={item === page ? 'active' : ''} type="button" key={item} onClick={() => setPage(item)}>
                {item}
              </button>
            ))}
            <button type="button" disabled={page === pageCount} onClick={() => setPage((value) => Math.min(pageCount, value + 1))}>
              下一页
              <ChevronRight size={16} />
            </button>
          </div>
        </section>

        <section className="library-updates-panel" aria-label="最近更新">
          <div className="library-updates-head">
            <div>
              <span>RECENT UPDATES</span>
              <h2>最近更新</h2>
            </div>
          </div>
          <div className="library-updates-table">
            <div className="library-update-row header">
              <span>类型</span>
              <span>书名</span>
              <span>最新章节</span>
              <span>作者</span>
              <span>更新时间</span>
            </div>
            {updateItems.slice(0, 6).map((item) => (
              <button className="library-update-row" type="button" key={item.id} onClick={() => goBookDetail(item.slug)}>
                <span>{item.type}</span>
                <strong>{item.title}</strong>
                <em>{item.chapter}</em>
                <span>{item.author}</span>
                <time>{item.updatedAt}</time>
              </button>
            ))}
            {!updateItems.length && (
              <div className="library-update-empty">暂无作品更新，发布作品后会自动出现在这里。</div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

const statusText: Record<string, string> = {
  paid: '已支付',
  pending: '待支付',
  refund_pending: '退款审核中',
  refunded: '已退款',
  refund_rejected: '退款已驳回',
  closed: '已关闭',
};

const statusClass = (status: string) => {
  if (status === 'paid') return 'paid';
  if (status === 'pending') return 'pending';
  if (status === 'refund_pending') return 'refund-pending';
  if (status === 'refunded') return 'refunded';
  if (status === 'refund_rejected') return 'refund-rejected';
  return '';
};

const formatInk = (value: number) => `${value > 0 ? '+' : ''}${value.toLocaleString()} 墨水`;
const ACCOUNT_PASSWORD_PATTERN = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]{8,32}$/;

const AccountShell: React.FC<SitePageProps & { active: 'account' | 'orders' | 'bookshelf' | 'profile'; children: React.ReactNode }> = (props) => {
  const [currentUser, setCurrentUser] = useCurrentUser();

  return (
    <div className="site-page account-page lumi-shell-page">
      <SiteHeader {...props} active={props.active} currentUser={currentUser} setCurrentUser={setCurrentUser} />
      <main className="account-main">
        {props.children}
      </main>
    </div>
  );
};

export const AccountCenterPage: React.FC<SitePageProps> = (props) => {
  const [currentUser] = useCurrentUser();
  const { assets, loading, error, reload } = useUserAssets(Boolean(currentUser));
  const [rechargeProducts, setRechargeProducts] = useState<RechargeProduct[]>([]);
  const [rechargeProductsLoading, setRechargeProductsLoading] = useState(false);
  const [selectedRechargeProduct, setSelectedRechargeProduct] = useState<RechargeProduct | null>(null);
  const [selectedRechargeChannel, setSelectedRechargeChannel] = useState('');
  const [rechargeSubmitting, setRechargeSubmitting] = useState(false);
  const [rechargeStatus, setRechargeStatus] = useState('');
  const [rechargePaymentUrl, setRechargePaymentUrl] = useState('');
  const [rechargeQrCode, setRechargeQrCode] = useState('');
  const [rechargeCopied, setRechargeCopied] = useState(false);
  const [rechargeSuccess, setRechargeSuccess] = useState(false);
  const displayName = currentUser?.nickname || currentUser?.username || '未登录用户';
  const account = assets.account;
  const accountStats: Array<[string, string, string, LucideIcon]> = [
    ['墨水余额', formatInkBalance(account.balance), `冻结 ${formatInkBalance(account.frozen)} 墨水`, WalletCards],
    ['会员状态', account.member_name || '体验用户', account.member_expires_at ? `${account.member_expires_at.slice(0, 10)} 到期` : '暂无到期时间', ShieldCheck],
    ['本月用量', `${account.monthly_used} / ${account.monthly_quota}`, '已生成章节额度', CreditCard],
    ['创作资产', `${assets.project_count} 本`, '已创建小说项目', BookMarked],
  ];

  useEffect(() => {
    if (!currentUser) return;
    setRechargeProductsLoading(true);
    apiRequest<{ products: RechargeProduct[] }>('/users_app/me/recharge-products')
      .then((result) => setRechargeProducts(result.products || []))
      .catch((err) => setRechargeStatus(err instanceof Error ? err.message : '充值套餐加载失败'))
      .finally(() => setRechargeProductsLoading(false));
  }, [currentUser]);

  const openRecharge = (product?: RechargeProduct) => {
    const selected = product || rechargeProducts.find((item) => item.featured) || rechargeProducts[0];
    if (!selected) {
      setRechargeStatus('暂无可用充值套餐，请稍后重试');
      return;
    }
    setSelectedRechargeProduct(selected);
    setSelectedRechargeChannel(selected.channels?.[0] || '');
    setRechargePaymentUrl('');
    setRechargeQrCode('');
    setRechargeCopied(false);
    setRechargeSuccess(false);
    setRechargeStatus(selected.channels?.length ? '' : '当前金额暂无可用支付渠道');
  };

  const closeRecharge = () => {
    if (rechargeSubmitting) return;
    setSelectedRechargeProduct(null);
    setSelectedRechargeChannel('');
    setRechargeStatus('');
    setRechargePaymentUrl('');
    setRechargeQrCode('');
    setRechargeCopied(false);
    setRechargeSuccess(false);
  };

  const copyRechargePaymentUrl = async () => {
    if (!rechargePaymentUrl) return;
    try {
      await navigator.clipboard.writeText(rechargePaymentUrl);
    } catch {
      const input = document.createElement('textarea');
      input.value = rechargePaymentUrl;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
    setRechargeCopied(true);
  };

  const submitSelectedRecharge = async () => {
    if (!selectedRechargeProduct || !selectedRechargeChannel || rechargeSubmitting) return;
    setRechargeSubmitting(true);
    setRechargeStatus('正在创建支付订单...');
    try {
      const result = await apiRequest<{ order: AssetOrder }>('/users_app/me/recharge', {
        method: 'POST',
        body: JSON.stringify({ productId: selectedRechargeProduct.id, channel: selectedRechargeChannel }),
      });
      const paymentUrl = String(result.order.paymentUrl || '').trim();
      setRechargePaymentUrl(paymentUrl);
      setRechargeQrCode(paymentUrl
        ? await QRCode.toDataURL(paymentUrl, {
          width: 220,
          margin: 1,
          color: { dark: '#17372e', light: '#ffffff' },
        }).catch(() => '')
        : '');
      setRechargeStatus(`订单 ${result.order.id} 已创建，正在等待北极星回调和墨水同步...`);
      await new Promise((resolve) => window.setTimeout(resolve, 3000));
      const order = await apiRequest<AssetOrder>(
        `/users_app/me/recharge-orders/${encodeURIComponent(result.order.id)}/simulate-success`,
        { method: 'POST' },
      );
      if (order.status !== 'paid') throw new Error('支付状态同步失败，请稍后重试');
      await reload();
      setRechargeStatus('');
      setRechargeSuccess(true);
      setRechargeSubmitting(false);
    } catch (err) {
      setRechargeStatus(err instanceof Error ? err.message : '充值失败，请稍后重试');
      setRechargeSubmitting(false);
    }
  };

  return (
    <AccountShell {...props} active="account">
      <section className="account-hero">
        <div className="account-hero-user">
          {currentUser ? <UserAvatar user={currentUser} /> : <span className="site-user-fallback">灵</span>}
          <div>
            <span>ACCOUNT CENTER</span>
            <h1>{displayName} 的账户中心</h1>
            <p>管理会员、墨水余额、充值和资产流水；个人资料与邮箱请前往个人设置。</p>
          </div>
        </div>
        <div className="account-hero-actions">
          <button className="lumi-btn lumi-btn-ghost" type="button" onClick={props.onOrders}>查看订单</button>
          <button className="lumi-btn lumi-btn-ghost" type="button" onClick={props.onProfile}>个人设置</button>
          <button className="lumi-btn lumi-btn-primary" type="button" disabled={rechargeProductsLoading} aria-busy={rechargeProductsLoading} onClick={() => openRecharge()}>
            充值墨水
          </button>
        </div>
      </section>
      {(loading || error) && <div className="account-message">{loading ? '正在同步资产...' : error}</div>}

      <section className="account-stat-grid">
        {accountStats.map(([label, value, desc, Icon]) => (
          <article className="account-stat-card" key={label as string}>
            <i><Icon size={20} /></i>
            <span>{label as string}</span>
            <strong>{value as string}</strong>
            <p>{desc as string}</p>
          </article>
        ))}
      </section>

      <section className="account-layout">
        <div className="account-panel">
          <div className="account-section-head">
            <div>
              <span>INK · 墨水充值</span>
              <h2>选择适合当前创作量的补给</h2>
            </div>
          </div>
          <div className="recharge-grid">
            {rechargeProducts.map((product) => (
              <button className={product.featured ? 'featured' : ''} type="button" key={product.id} onClick={() => openRecharge(product)}>
                <span>{product.name}</span>
                <strong>¥{(product.payAmount / 100).toFixed(2)}</strong>
                <em>{product.amount.toLocaleString()} {product.unit || '墨水'}</em>
                <p>{product.description || '充值成功后由内容中台回调同步到账'}</p>
              </button>
            ))}
            {!rechargeProductsLoading && !rechargeProducts.length && <p>暂无可用充值套餐</p>}
          </div>
        </div>

        <aside className="account-side-stack">
          <section className="account-mini-panel">
            <div className="account-mini-title"><Gift size={18} /><strong>最近墨水流水</strong></div>
            {assets.records.length ? (
              <div className="asset-record-list">
                {assets.records.slice(0, 5).map((record) => (
                  <article className="asset-record-card" key={record.id}>
                    <span>{record.type}</span>
                    <strong className={record.amount >= 0 ? 'positive' : 'negative'}>{formatInk(record.amount)}</strong>
                    <p>{record.reason}</p>
                    {typeof record.cost_yuan === 'number' && (
                      <small>QuickRouter 实际费用 ¥{record.cost_yuan.toFixed(6)}{record.model ? ` · ${record.model}` : ''}</small>
                    )}
                    <em>{record.createdAt}</em>
                  </article>
                ))}
              </div>
            ) : (
              <p>暂无墨水流水。领取福利、后台划转或充值成功后会出现在这里。</p>
            )}
          </section>
          <section className="account-mini-panel account-asset-note">
            <div className="account-mini-title"><ShieldCheck size={18} /><strong>资产说明</strong></div>
            <p>墨水用于 AI 生成和后续增值服务。充值按 1 元 = 10 墨水展示；AI 消耗按 QuickRouter 实际模型单价和 Token 用量结算，每一次变化都会生成流水。</p>
            <button type="button" onClick={() => { void reload(); }}>刷新资产</button>
          </section>
        </aside>
      </section>
      {selectedRechargeProduct && (
        <div className="payment-channel-overlay" onClick={(event) => event.target === event.currentTarget && closeRecharge()}>
          <section className="payment-channel-dialog" role="dialog" aria-modal="true" aria-label="选择支付方式">
            <header>
              <div>
                <h2>选择支付方式</h2>
              </div>
              <button type="button" disabled={rechargeSubmitting} onClick={closeRecharge}>关闭</button>
            </header>
            <div className="payment-order-summary">
              <strong>{selectedRechargeProduct.name}</strong>
              <span>{selectedRechargeProduct.amount.toLocaleString()} {selectedRechargeProduct.unit || '墨水'}</span>
              <em>¥{(selectedRechargeProduct.payAmount / 100).toFixed(2)}</em>
            </div>
            {!rechargePaymentUrl && !rechargeSuccess && <div className="payment-channel-options">
              {selectedRechargeProduct.channels.map((channel) => (
                <button
                  type="button"
                  key={channel}
                  className={selectedRechargeChannel === channel ? 'active' : ''}
                  disabled={rechargeSubmitting}
                  onClick={() => setSelectedRechargeChannel(channel)}
                >
                  <i>{channel.startsWith('ali') ? '支' : '微'}</i>
                  <span>{channel.startsWith('ali') ? '支付宝' : channel === 'wechat' ? '微信支付' : channel}</span>
                  <em>{selectedRechargeChannel === channel ? '✓' : ''}</em>
                </button>
              ))}
            </div>}
            {rechargePaymentUrl && !rechargeSuccess && (
              <div className="payment-qr-card">
                {rechargeQrCode && <img src={rechargeQrCode} alt="支付二维码" />}
                <strong>{selectedRechargeChannel.startsWith('ali') ? '支付宝' : '微信支付'}</strong>
                <button type="button" onClick={() => { void copyRechargePaymentUrl(); }}>
                  {rechargeCopied ? '已复制' : '复制支付地址'}
                </button>
                <small>{rechargePaymentUrl}</small>
              </div>
            )}
            {rechargeSuccess && (
              <div className="payment-success-state">
                <h2>支付成功</h2>
                <span><CheckCircle2 size={42} strokeWidth={3} /></span>
                <strong>
                  {selectedRechargeProduct.amount.toLocaleString()} {selectedRechargeProduct.unit || '墨水'}已到账
                </strong>
                <p>可在账户余额与充值记录中查看</p>
              </div>
            )}
            {rechargeStatus && !rechargeSuccess && <p className="payment-channel-status">{rechargeStatus}</p>}
            <button
              className="payment-channel-submit"
              type="button"
              disabled={(rechargeSubmitting && !rechargeSuccess) || (!selectedRechargeChannel && !rechargeSuccess)}
              onClick={() => {
                if (rechargeSuccess) closeRecharge();
                else void submitSelectedRecharge();
              }}
            >
              {rechargeSubmitting ? '等待支付回调...' : rechargeSuccess ? '完成' : '创建支付订单'}
            </button>
          </section>
        </div>
      )}
    </AccountShell>
  );
};

export const OrdersPage: React.FC<SitePageProps> = (props) => {
  const [currentUser] = useCurrentUser();
  const { assets, loading, error, reload } = useUserAssets(Boolean(currentUser));
  const [filter, setFilter] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [refundOrder, setRefundOrder] = useState<AssetOrder | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundMessage, setRefundMessage] = useState('');
  const orders = assets.orders.filter((order) => {
    const matchedStatus = filter === 'all' || order.status === filter;
    const matchedKeyword = !keyword || order.id.includes(keyword) || order.product.includes(keyword);
    return matchedStatus && matchedKeyword;
  });

  const openRefund = (order: AssetOrder) => {
    setRefundOrder(order);
    setRefundReason('');
    setRefundMessage('');
  };

  const closeRefund = () => {
    if (refundBusy) return;
    setRefundOrder(null);
    setRefundReason('');
    setRefundMessage('');
  };

  const submitRefund = async () => {
    if (!refundOrder || refundReason.trim().length < 2) {
      setRefundMessage('请填写至少 2 个字的退款原因');
      return;
    }
    setRefundBusy(true);
    setRefundMessage('');
    try {
      const result = await apiRequest<{ message: string }>(`/users_app/me/orders/${encodeURIComponent(refundOrder.id)}/refund`, {
        method: 'POST',
        body: JSON.stringify({ reason: refundReason.trim() }),
      });
      setRefundMessage(result.message || '退款申请已提交');
      await reload();
      window.setTimeout(() => {
        setRefundOrder(null);
        setRefundReason('');
        setRefundMessage('');
      }, 900);
    } catch (err) {
      setRefundMessage(err instanceof Error ? err.message : '退款申请提交失败');
    } finally {
      setRefundBusy(false);
    }
  };

  return (
    <AccountShell {...props} active="orders">
      <section className="orders-hero">
        <div>
          <span>ORDERS · 我的订单</span>
          <h1>会员、墨水和消费记录</h1>
          <p>在这里查看您的会员开通、墨水获取及其他订单记录。</p>
        </div>
        <button className="lumi-btn lumi-btn-primary" type="button" onClick={props.onAccount}>返回账户中心</button>
      </section>

      {(loading || error) && <div className="account-message">{loading ? '正在同步订单...' : error}</div>}
      <section className="orders-toolbar">
        <div>
          {[
            ['all', '全部'],
            ['paid', '已支付'],
            ['pending', '待支付'],
            ['refund_pending', '退款中'],
            ['refunded', '已退款'],
            ['refund_rejected', '已驳回'],
          ].map(([id, label]) => (
            <button className={filter === id ? 'active' : ''} type="button" key={id} onClick={() => setFilter(id)}>{label}</button>
          ))}
        </div>
        <label>
          <Search size={16} />
          <input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="搜索订单号 / 商品名称" />
        </label>
      </section>

      <section className="orders-list">
        {orders.length ? orders.map((order) => (
          <article className="order-card" key={order.id}>
            <div className="order-main">
              <i><ReceiptText size={20} /></i>
              <div>
                <span>{order.id}</span>
                <strong>{order.product}</strong>
                <p>{order.paidAt} · {order.channel}</p>
                {order.refundReason && (
                  <p className="order-refund-detail">
                    退款原因：{order.refundReason}
                    {order.refundRemark ? ` · 处理说明：${order.refundRemark}` : ''}
                  </p>
                )}
              </div>
            </div>
            <div className="order-meta">
              <span>{order.ink ? formatInk(order.ink) : '会员权益'}</span>
              <strong>¥{Number(order.amount || 0).toFixed(2)}</strong>
              <em className={statusClass(order.status)}>{statusText[order.status] || order.status}</em>
              {order.canRefund && (
                <button className="order-refund-button" type="button" onClick={() => openRefund(order)}>
                  申请退款
                </button>
              )}
            </div>
          </article>
        )) : (
          <div className="account-empty-state">暂无相关订单记录</div>
        )}
      </section>

      {refundOrder && (
        <div className="account-dialog-backdrop" role="dialog" aria-modal="true" aria-label="申请退款" onClick={closeRefund}>
          <section className="account-dialog refund-dialog" onClick={(event) => event.stopPropagation()}>
            <button className="account-dialog-close" type="button" aria-label="关闭" onClick={closeRefund}>
              <X size={16} />
            </button>
            <h3>申请订单退款</h3>
            <p>
              订单 {refundOrder.id} 审核期间将冻结 {refundOrder.ink.toLocaleString()} 墨水。
              审核通过后扣回，驳回后自动解冻。
            </p>
            <label>
              退款原因
              <textarea
                value={refundReason}
                maxLength={200}
                placeholder="请说明退款原因，便于工作人员审核"
                onChange={(event) => setRefundReason(event.target.value)}
              />
            </label>
            <div className="refund-dialog-count">{refundReason.length} / 200</div>
            <button
              className="account-dialog-primary danger"
              type="button"
              disabled={refundBusy || refundReason.trim().length < 2}
              onClick={submitRefund}
            >
              {refundBusy ? '正在提交' : '确认提交退款申请'}
            </button>
            {refundMessage && <div className="account-dialog-message">{refundMessage}</div>}
          </section>
        </div>
      )}
    </AccountShell>
  );
};

export const BookshelfPage: React.FC<SitePageProps> = (props) => {
  const [currentUser] = useCurrentUser();
  const [books, setBooks] = useState<ShelfBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const loadBookshelf = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setBooks(await apiRequest<ShelfBook[]>('/novel_project_app/library/my-bookshelf'));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '书架加载失败');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    void loadBookshelf();
  }, [loadBookshelf]);

  const removeBook = async (slug: string) => {
    try {
      await apiRequest(`/novel_project_app/library/books/${encodeURIComponent(slug)}/bookshelf`, { method: 'DELETE' });
      setBooks((current) => current.filter((book) => book.slug !== slug));
      setMessage('已从书架移除');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '移除失败');
    }
  };

  return (
    <AccountShell {...props} active="bookshelf">
      <section className="bookshelf-hero">
        <div>
          <span>BOOKSHELF · 我的书架</span>
          <h1>把喜欢的故事留在手边</h1>
          <p>收藏的作品会在这里集中展示，随时继续阅读，也可以轻松整理。</p>
        </div>
        <button className="lumi-btn lumi-btn-primary" type="button" onClick={props.onLibrary}>发现更多好书</button>
      </section>
      {message && <div className="account-message">{message}</div>}
      {loading ? <div className="bookshelf-empty">正在整理您的书架...</div> : books.length ? (
        <section className="my-bookshelf-grid">
          {books.map((book) => (
            <article className="my-bookshelf-card" key={book.slug}>
              <button className="my-bookshelf-cover" type="button" onClick={() => goBookDetail(book.slug)}>
                <img src={book.cover} alt={book.title} />
                <span>继续阅读</span>
              </button>
              <div>
                <small>{book.category} · {book.status}</small>
                <h2>{book.title}</h2>
                <p>{book.author}</p>
                <div>
                  <button type="button" onClick={() => goBookDetail(book.slug)}>查看作品</button>
                  <button className="remove" type="button" onClick={() => void removeBook(book.slug)}>移出书架</button>
                </div>
              </div>
            </article>
          ))}
        </section>
      ) : (
        <div className="bookshelf-empty">
          <BookMarked size={38} />
          <h2>书架还是空的</h2>
          <p>阅读时点击带“+”的书架按钮，就能把作品收藏到这里。</p>
          <button className="lumi-btn lumi-btn-primary" type="button" onClick={props.onLibrary}>去书阁看看</button>
        </div>
      )}
    </AccountShell>
  );
};

export const ProfileSettingsPage: React.FC<SitePageProps> = (props) => {
  const [currentUser, setCurrentUser] = useCurrentUser();
  const displayName = currentUser?.nickname || currentUser?.username || '未登录用户';
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [nickname, setNickname] = useState(currentUser?.nickname || currentUser?.username || '');
  const [nicknameSaving, setNicknameSaving] = useState(false);
  const [nicknameMessage, setNicknameMessage] = useState('');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [generationSettings, setGenerationSettings] = useState<UserGenerationSettings | null>(null);
  const [defaultChapters, setDefaultChapters] = useState(50);
  const [chapterSettingsSaving, setChapterSettingsSaving] = useState(false);
  const [chapterSettingsMessage, setChapterSettingsMessage] = useState('');

  useEffect(() => {
    if (!localStorage.getItem('lingbi-token')) return;
    let cancelled = false;
    apiRequest<StoredUser>('/users_app/me')
      .then((user) => {
        if (cancelled) return;
        localStorage.setItem('lingbi-user', JSON.stringify(user));
        setCurrentUser(user);
        setNickname(user.nickname || user.username || '');
        setEmail(user.email || '');
        window.dispatchEvent(new Event('lingbi-auth-change'));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [setCurrentUser]);

  useEffect(() => {
    if (!localStorage.getItem('lingbi-token')) return;
    let cancelled = false;
    apiRequest<UserGenerationSettings>('/novel_project_app/generation-settings')
      .then((settings) => {
        if (cancelled) return;
        setGenerationSettings(settings);
        setDefaultChapters(settings.default_chapters || 50);
      })
      .catch((error) => {
        if (!cancelled) setChapterSettingsMessage(error instanceof Error ? error.message : '创作偏好加载失败');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const logoutToLogin = () => {
    localStorage.removeItem('lingbi-token');
    localStorage.removeItem('lingbi-user');
    setCurrentUser(null);
    window.location.hash = '#login';
  };

  const submitPassword = async () => {
    if (!currentUser) {
      props.onLogin();
      return;
    }
    if (!oldPassword || !newPassword || !confirmPassword || saving) return;
    if (!ACCOUNT_PASSWORD_PATTERN.test(newPassword)) {
      setMessage('新密码需为 8-32 位，至少包含字母和数字，不能包含中文或空格。');
      return;
    }
    setSaving(true);
    setMessage('正在修改密码...');
    try {
      const result = await apiRequest<{ message: string }>('/users_app/me/password', {
        method: 'PUT',
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
      setMessage(result.message || '密码已修改，请重新登录。');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      window.setTimeout(logoutToLogin, 800);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '修改密码失败');
    } finally {
      setSaving(false);
    }
  };

  const submitEmail = async () => {
    const normalized = email.trim().toLowerCase();
    if (!currentUser) {
      props.onLogin();
      return;
    }
    if (!normalized || emailSaving) return;
    setEmailSaving(true);
    setEmailMessage('正在保存邮箱...');
    try {
      const user = await apiRequest<StoredUser>('/users_app/me/email', {
        method: 'PUT',
        body: JSON.stringify({ email: normalized }),
      });
      localStorage.setItem('lingbi-user', JSON.stringify(user));
      setCurrentUser(user);
      setEmail(user.email || normalized);
      setEmailMessage(currentUser.email ? '邮箱已更新。' : '邮箱已绑定。');
      window.dispatchEvent(new Event('lingbi-auth-change'));
    } catch (error) {
      setEmailMessage(error instanceof Error ? error.message : '邮箱保存失败');
    } finally {
      setEmailSaving(false);
    }
  };

  const submitNickname = async () => {
    const normalized = nickname.trim();
    if (!currentUser) {
      props.onLogin();
      return;
    }
    if (!normalized || nicknameSaving) return;
    if (normalized.length > 20) {
      setNicknameMessage('昵称不能超过 20 个字符。');
      return;
    }
    if (normalized === (currentUser.nickname || currentUser.username)) {
      setNicknameMessage('昵称没有变化。');
      return;
    }
    setNicknameSaving(true);
    setNicknameMessage('正在保存昵称...');
    try {
      const user = await apiRequest<StoredUser>('/users_app/me/nickname', {
        method: 'PUT',
        body: JSON.stringify({ nickname: normalized }),
      });
      localStorage.setItem('lingbi-user', JSON.stringify(user));
      setCurrentUser(user);
      setNickname(user.nickname || user.username || normalized);
      setNicknameMessage('昵称已更新。');
      window.dispatchEvent(new Event('lingbi-auth-change'));
    } catch (error) {
      setNicknameMessage(error instanceof Error ? error.message : '昵称保存失败');
    } finally {
      setNicknameSaving(false);
    }
  };

  const submitGenerationSettings = async () => {
    if (!currentUser) {
      props.onLogin();
      return;
    }
    const maxChapters = generationSettings?.max_chapters || 200;
    if (!Number.isInteger(defaultChapters) || defaultChapters < 3 || defaultChapters > maxChapters) {
      setChapterSettingsMessage(`默认章节数必须是 3–${maxChapters} 之间的整数。`);
      return;
    }
    setChapterSettingsSaving(true);
    setChapterSettingsMessage('正在保存创作偏好...');
    try {
      const settings = await apiRequest<UserGenerationSettings>('/novel_project_app/generation-settings', {
        method: 'PUT',
        body: JSON.stringify({ default_chapters: defaultChapters }),
      });
      setGenerationSettings(settings);
      setDefaultChapters(settings.default_chapters);
      setChapterSettingsMessage('已保存；以后新建小说将使用这个默认章节数。');
    } catch (error) {
      setChapterSettingsMessage(error instanceof Error ? error.message : '创作偏好保存失败');
    } finally {
      setChapterSettingsSaving(false);
    }
  };

  return (
    <AccountShell {...props} active="profile">
      <section className="orders-hero profile-hero">
        <div>
          <span>PROFILE · 个人设置</span>
          <h1>{displayName} 的个人设置</h1>
          <p>管理基础资料、个人创作偏好与登录安全。余额、会员和流水仍然放在账户中心。</p>
        </div>
        <button className="lumi-btn lumi-btn-primary" type="button" onClick={props.onAccount}>返回账户中心</button>
      </section>

      <section className="profile-settings-grid">
        <div className="account-panel profile-security-card">
          <div className="account-section-head">
            <div>
              <span>SECURITY</span>
              <h2>修改登录密码</h2>
            </div>
            <em>修改成功后会退出当前登录，请用新密码重新进入。</em>
          </div>
          <div className="profile-password-form">
            <label>
              旧密码
              <input type="password" value={oldPassword} onChange={(event) => setOldPassword(event.target.value)} placeholder="请输入当前密码" />
            </label>
            <label>
              新密码
              <input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={8} maxLength={32} placeholder="8-32 位，含字母和数字" />
            </label>
            <label>
              确认新密码
              <input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} minLength={8} maxLength={32} placeholder="再次输入新密码" />
            </label>
            <button className="lumi-btn lumi-btn-primary" type="button" disabled={saving || !oldPassword || !newPassword || !confirmPassword} onClick={submitPassword}>
              <KeyRound size={16} />
              {saving ? '修改中' : '确认修改密码'}
            </button>
          </div>
          {message && <div className="account-message">{message}</div>}
        </div>

        <aside className="account-side-stack">
          <section className="account-mini-panel">
            <div className="account-mini-title"><Settings size={18} /><strong>基础资料</strong></div>
            <p className="profile-readonly-note"><LockKeyhole size={14} />用户名是登录账号，创建后不可修改；昵称可以随时修改。</p>
            <label>
              用户名
              <input value={currentUser?.username || ''} readOnly aria-readonly="true" title="用户名创建后不可修改" />
            </label>
            <label>
              昵称
              <input
                value={nickname}
                maxLength={20}
                placeholder={currentUser?.username || '请输入昵称'}
                onChange={(event) => {
                  setNickname(event.target.value);
                  setNicknameMessage('');
                }}
              />
            </label>
            <button type="button" disabled={nicknameSaving || !nickname.trim()} onClick={submitNickname}>
              {nicknameSaving ? '保存中' : '保存昵称'}
            </button>
            {nicknameMessage && <div className="profile-email-message">{nicknameMessage}</div>}
            <div className="profile-email-divider" />
            <label>
              绑定邮箱
              <input
                type="email"
                value={email}
                maxLength={120}
                autoComplete="email"
                placeholder="请输入常用邮箱"
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <button type="button" disabled={emailSaving || !email.trim()} onClick={submitEmail}>
              {emailSaving ? '保存中' : currentUser?.email ? '更新邮箱' : '绑定邮箱'}
            </button>
            {emailMessage && <div className="profile-email-message">{emailMessage}</div>}
          </section>
          <section className="account-mini-panel">
            <div className="account-mini-title"><BookOpen size={18} /><strong>创作偏好</strong></div>
            <p>
              设置新建小说的默认章节数。系统默认 {generationSettings?.system_default_chapters ?? 50} 章，
              管理员允许最多 {generationSettings?.max_chapters ?? 200} 章；具体作品仍可在工作台设定页单独修改。
            </p>
            <label>
              默认生成章节数
              <input
                type="number"
                min={3}
                max={generationSettings?.max_chapters ?? 200}
                step={1}
                value={defaultChapters}
                onChange={(event) => setDefaultChapters(Number(event.target.value))}
              />
            </label>
            <button type="button" disabled={chapterSettingsSaving} onClick={submitGenerationSettings}>
              {chapterSettingsSaving ? '保存中' : '保存创作偏好'}
            </button>
            {chapterSettingsMessage && <div className="profile-email-message">{chapterSettingsMessage}</div>}
          </section>
          <section className="account-mini-panel">
            <div className="account-mini-title"><ShieldCheck size={18} /><strong>安全设置</strong></div>
            <p>登录密码用于保护创作项目、资产余额和发布内容。注销账户仍在右上角用户菜单里处理，避免误触。</p>
            <button type="button" onClick={props.onAccount}>查看资产账户</button>
          </section>
        </aside>
      </section>
    </AccountShell>
  );
};

export const PaymentPage: React.FC<SitePageProps & { planId?: string | null; billingCycle?: BillingCycle }> = (props) => {
  const [currentUser, setCurrentUser] = useCurrentUser();
  useEffect(() => {
    if (currentUser) window.location.hash = '#account';
  }, [currentUser]);

  return (
    <div className="site-page payment-page lumi-shell-page">
      <SiteHeader {...props} active="account" currentUser={currentUser} setCurrentUser={setCurrentUser} />
      <main className="payment-main">
        <section className="payment-hero">
          <div>
            <span>PAYMENT · 北极星支付</span>
            <h1>正在进入资产账户</h1>
            <p>充值套餐沿用小说原配置，可用支付渠道按当前套餐金额从内容中台实时校验。</p>
          </div>
          <div className="payment-secure">
            <ShieldCheck size={20} />
            <span>金额校验 · 创建订单 · 回调到账</span>
          </div>
        </section>
        <button className="lumi-btn lumi-btn-primary" type="button" onClick={() => currentUser ? (window.location.hash = '#account') : props.onLogin()}>
          {currentUser ? '进入资产账户' : '登录后充值'}
        </button>
      </main>
    </div>
  );
};

export const BookDetailPage: React.FC<SitePageProps & { slug?: string | null }> = (props) => {
  const [currentUser, setCurrentUser] = useCurrentUser();
  const [introExpanded, setIntroExpanded] = useState(false);
  const { book, loading } = useLibraryBook(props.slug);
  const firstChapter = book.chapters[0];
  const introNeedsToggle = book.intro.length > 120;

  useEffect(() => {
    setIntroExpanded(false);
  }, [book.slug]);

  useEffect(() => {
    const description = (book.intro || book.desc || `${book.title}在线阅读`).slice(0, 160);
    const path = `/library/book/${encodeURIComponent(book.slug)}`;
    setSeoMetadata({
      title: `${book.title}最新章节｜${book.author} - 灵笔书阁`,
      description,
      path,
      image: book.cover,
      type: 'book',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Book',
        name: book.title,
        author: { '@type': 'Person', name: book.author },
        description,
        image: new URL(book.cover, window.location.origin).toString(),
        url: new URL(path, window.location.origin).toString(),
        genre: book.tags,
        inLanguage: 'zh-CN',
      },
    });
  }, [book]);

  return (
    <div className="site-page book-detail-page lumi-shell-page">
      <SiteHeader {...props} active="library" currentUser={currentUser} setCurrentUser={setCurrentUser} />
      <main className="book-detail-main">
        <div className="book-crumb">
          <button type="button" onClick={props.onLibrary}>书阁</button>
          <span>/</span>
          <span>{book.title}</span>
        </div>

        <section className="book-hero-panel">
          <img className="book-detail-cover" src={book.cover} alt={book.title} />
          <div className="book-detail-info">
            <div className="book-detail-title-row">
              <h1>{book.title}</h1>
              <span>{book.status}</span>
            </div>
            {loading ? <p className="book-detail-author">正在同步书阁作品...</p> : null}
            <div className="book-detail-tags">
              {book.tags.map((tag) => <span key={tag}>{tag}</span>)}
            </div>
            <div className="book-stats">
              <span>{book.words}</span>
              <span>{book.reader}</span>
              <span>最新：{book.latestChapter}</span>
              <span>{book.update}</span>
            </div>
            <div className="book-detail-actions">
              <button className="read-now-button" type="button" disabled={!firstChapter || loading} onClick={() => firstChapter && goReader(book.slug, firstChapter.no)}>
                <BookOpen size={18} />
                {firstChapter ? '开始阅读' : '同步目录中'}
              </button>
            </div>
          </div>
          <aside className="book-author-card">
            <img src={book.avatar} alt={book.author} />
            <strong>{book.author}</strong>
            <p>{book.authorBio}</p>
          </aside>
        </section>

        <section className="book-section-card book-intro-section">
          <div className="book-section-title">
            <h2>作品简介</h2>
          </div>
          <p className={introNeedsToggle && !introExpanded ? 'book-intro-text collapsed' : 'book-intro-text'}>{book.intro}</p>
          {introNeedsToggle && (
            <button className="book-intro-toggle" type="button" onClick={() => setIntroExpanded((value) => !value)}>
              {introExpanded ? '收起简介' : '展开简介'}
            </button>
          )}
        </section>

        <section className="book-section-card book-catalog-section" id="book-catalog">
          <div className="book-section-title">
            <h2>目录</h2>
            <span>共 {book.chapters.length} 章，点击章节可直接阅读</span>
          </div>
          <div className="book-catalog-grid">
            {book.chapters.length ? book.chapters.map((chapter) => (
              <button
                type="button"
                key={chapter.no}
                onClick={() => goReader(book.slug, chapter.no)}
              >
                <span className="book-catalog-chapter">
                  {chapter.locked && <LockKeyhole size={15} aria-label="付费章节" />}
                  <span>{chapter.title}</span>
                </span>
                <em>{chapter.locked ? `解锁 ${chapter.price || book.paywall?.chapter_price || 0} 墨水` : `${chapter.words}字`}</em>
              </button>
            )) : <div className="book-catalog-empty">正在同步作者发布的目录...</div>}
          </div>
        </section>
      </main>
    </div>
  );
};

const ReaderVerticalAdvertisement: React.FC<{
  side: 'left' | 'right';
  provider: string;
  imageUrl: string;
  targetUrl: string;
  adUnitId: string;
  alt: string;
}> = ({ side, provider, imageUrl, targetUrl, adUnitId, alt }) => {
  if (!imageUrl) return null;
  const creative = (
    <>
      <span>广告</span>
      <img src={imageUrl} alt={alt || `${side === 'left' ? '左侧' : '右侧'}广告`} loading="lazy" />
    </>
  );
  return (
    <aside
      className={`reader-ad-slot reader-ad-slot-${side}`}
      aria-label={side === 'left' ? '左侧广告' : '右侧广告'}
      data-ad-provider={provider}
      data-ad-unit-id={adUnitId || undefined}
    >
      {targetUrl ? (
        <a href={targetUrl} target="_blank" rel="noopener noreferrer sponsored">
          {creative}
        </a>
      ) : (
        <div>{creative}</div>
      )}
    </aside>
  );
};

export const ReaderPage: React.FC<SitePageProps & { slug?: string | null; chapterNo?: number }> = (props) => {
  const [currentUser] = useCurrentUser();
  const [readerDark, setReaderDark] = useState(() => localStorage.getItem('lingbi-reader-theme') === 'dark');
  const [fontLevel, setFontLevel] = useState(() => {
    const storedValue = localStorage.getItem('lingbi-reader-font-level');
    if (storedValue === null) return 2;
    const stored = Number(storedValue);
    return Number.isInteger(stored) && stored >= 0 && stored <= 4 ? stored : 2;
  });
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [fontPanelOpen, setFontPanelOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockMessage, setUnlockMessage] = useState('');
  const [inBookshelf, setInBookshelf] = useState(false);
  const [bookshelfBusy, setBookshelfBusy] = useState(false);
  const [bookshelfMessage, setBookshelfMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [readerAdvertisements, setReaderAdvertisements] = useState<ReaderAdvertisementSettings>(disabledReaderAdvertisements);
  const { book } = useLibraryBook(props.slug);
  const { chapter, loading: chapterLoading, setChapter } = useLibraryChapter(props.slug, props.chapterNo || 1, book);
  const previousChapter = book.chapters.find((item) => item.no === chapter.no - 1);
  const nextChapter = book.chapters.find((item) => item.no === chapter.no + 1);
  const currentChapterIndex = book.chapters.findIndex((item) => item.no === chapter.no);
  const currentChapterPosition = currentChapterIndex >= 0 ? currentChapterIndex + 1 : Math.max(1, chapter.no);
  const readingProgress = book.chapters.length
    ? Math.round((Math.min(currentChapterPosition, book.chapters.length) / book.chapters.length) * 100)
    : 0;
  const showLeftAdvertisement = readerAdvertisements.enabled
    && readerAdvertisements.left_enabled
    && Boolean(readerAdvertisements.left_image_url);
  const showRightAdvertisement = readerAdvertisements.enabled
    && readerAdvertisements.right_enabled
    && Boolean(readerAdvertisements.right_image_url);

  useEffect(() => {
    let active = true;
    apiRequest<ReaderAdvertisementSettings>('/novel_project_app/library/reader-advertisements')
      .then((result) => {
        if (active) setReaderAdvertisements(result);
      })
      .catch(() => {
        if (active) setReaderAdvertisements(disabledReaderAdvertisements);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const path = `/library/read/${encodeURIComponent(book.slug)}/${chapter.no}`;
    const description = `${book.title} ${chapter.title}在线阅读，作者：${book.author}。`;
    setSeoMetadata({
      title: `${chapter.title}｜${book.title} - 灵笔书阁`,
      description,
      path,
      image: book.cover,
      type: 'article',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Chapter',
        name: chapter.title,
        position: chapter.no,
        isPartOf: { '@type': 'Book', name: book.title, author: { '@type': 'Person', name: book.author } },
        url: new URL(path, window.location.origin).toString(),
        inLanguage: 'zh-CN',
      },
    });
  }, [book.author, book.cover, book.slug, book.title, chapter.no, chapter.title]);

  useEffect(() => {
    localStorage.setItem('lingbi-reader-theme', readerDark ? 'dark' : 'light');
  }, [readerDark]);

  useEffect(() => {
    localStorage.setItem('lingbi-reader-font-level', String(fontLevel));
  }, [fontLevel]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCatalogOpen(false);
  }, [chapter.no]);

  useEffect(() => {
    if (!currentUser || !book.slug || book.slug === 'loading') return;
    apiRequest<{ inBookshelf: boolean }>(`/novel_project_app/library/books/${encodeURIComponent(book.slug)}/bookshelf`)
      .then((result) => setInBookshelf(result.inBookshelf))
      .catch(() => setInBookshelf(false));
  }, [book.slug, currentUser]);

  useEffect(() => {
    if (!bookshelfMessage) return undefined;
    const timer = window.setTimeout(() => setBookshelfMessage(null), 3000);
    return () => window.clearTimeout(timer);
  }, [bookshelfMessage]);

  const toggleBookshelf = async () => {
    if (!currentUser || !localStorage.getItem('lingbi-token')) {
      props.onLogin();
      return;
    }
    if (bookshelfBusy) return;
    setBookshelfBusy(true);
    try {
      const result = await apiRequest<{ inBookshelf: boolean }>(
        `/novel_project_app/library/books/${encodeURIComponent(book.slug)}/bookshelf`,
        { method: inBookshelf ? 'DELETE' : 'POST' },
      );
      setInBookshelf(result.inBookshelf);
      setBookshelfMessage({
        type: 'success',
        text: result.inBookshelf ? '已加入我的书架' : '已从书架移除',
      });
    } catch (error) {
      setBookshelfMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '书架操作失败，请稍后重试',
      });
    } finally {
      setBookshelfBusy(false);
    }
  };

  const goChapter = (chapterNo: number) => {
    goReader(book.slug, chapterNo);
  };

  const unlockChapter = async () => {
    if (!currentUser) {
      props.onLogin();
      return;
    }
    const price = chapter.price || book.paywall?.chapter_price || 0;
    if (!window.confirm(`确认使用 ${price.toLocaleString()} 墨水解锁本章吗？解锁后可永久阅读。`)) return;
    setUnlocking(true);
    setUnlockMessage('');
    try {
      const result = await apiRequest<{ message: string; balance: number; chapter: ShelfChapter }>(
        `/novel_project_app/library/books/${encodeURIComponent(book.slug)}/chapters/${chapter.no}/unlock`,
        { method: 'POST' },
      );
      setChapter(result.chapter);
      setUnlockMessage(`${result.message}，剩余 ${formatInkBalance(result.balance)} 墨水`);
    } catch (error) {
      setUnlockMessage(error instanceof Error ? error.message : '章节解锁失败，请稍后重试');
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <div className={`reader-page ${readerDark ? 'dark' : ''} ${showRightAdvertisement ? 'has-right-ad' : ''}`}>
      <div className="reader-progress" aria-label={`阅读进度 ${readingProgress}%`}>
        <span style={{ width: `${readingProgress}%` }} />
      </div>
      <header className="reader-topbar">
        <span className="reader-topbar-spacer" aria-hidden="true" />
        <div>
          <strong>{book.title}</strong>
          <span>{chapter.title}</span>
        </div>
        {currentUser ? (
          <button className="reader-user" type="button">
            <UserAvatar user={currentUser} />
            <span>{currentUser.nickname || currentUser.username}</span>
          </button>
        ) : (
          <button type="button" onClick={props.onLogin}>登录</button>
        )}
      </header>

      {showLeftAdvertisement && (
        <ReaderVerticalAdvertisement
          side="left"
          provider={readerAdvertisements.provider}
          imageUrl={readerAdvertisements.left_image_url}
          targetUrl={readerAdvertisements.left_target_url}
          adUnitId={readerAdvertisements.left_ad_unit_id}
          alt={readerAdvertisements.left_alt}
        />
      )}
      {showRightAdvertisement && (
        <ReaderVerticalAdvertisement
          side="right"
          provider={readerAdvertisements.provider}
          imageUrl={readerAdvertisements.right_image_url}
          targetUrl={readerAdvertisements.right_target_url}
          adUnitId={readerAdvertisements.right_ad_unit_id}
          alt={readerAdvertisements.right_alt}
        />
      )}

      <main className={`reader-shell font-level-${fontLevel}`}>
        <div className="reader-back-row">
          <button className="reader-back-button" type="button" onClick={() => goBookDetail(book.slug)}>
            <ArrowLeft size={18} />
            返回作品
          </button>
        </div>
        <article className="reader-paper">
          <div className="reader-chapter-head">
            <span>第 {currentChapterPosition} / {Math.max(book.chapters.length, currentChapterPosition)} 章</span>
            <h1>{chapter.title.replace(/^第\d+章\s*/, '')}</h1>
            <p>{chapterLoading ? '正在同步正文...' : `${chapter.words} 字 · ${chapter.updatedAt}`}</p>
          </div>
          <div className="reader-content">
            {chapterLoading ? (
              <p>正在同步正文...</p>
            ) : chapter.content.length ? (
              chapter.content.map((paragraph) => <p key={paragraph}>{paragraph}</p>)
            ) : (
              <p>本章暂时没有可阅读的正文内容。</p>
            )}
          </div>
          {chapter.locked && (
            <section className="reader-paywall">
              <div className="reader-paywall-icon">
                <KeyRound size={22} />
              </div>
              <span>本章为付费章节</span>
              <h2>继续阅读完整内容</h2>
              <p>解锁后永久有效，再次阅读不会重复扣费。</p>
              <button type="button" disabled={unlocking} onClick={unlockChapter}>
                <WalletCards size={18} />
                {currentUser
                  ? unlocking
                    ? '正在解锁...'
                    : `使用 ${(chapter.price || book.paywall?.chapter_price || 0).toLocaleString()} 墨水解锁`
                  : '登录后解锁本章'}
              </button>
            </section>
          )}
          {unlockMessage && (
            <div className={`reader-unlock-message ${chapter.locked ? 'error' : 'success'}`} role="status">
              {unlockMessage}
            </div>
          )}
          <div className="reader-next-row">
            <button className="reader-chapter-button secondary" type="button" disabled={!previousChapter} onClick={() => previousChapter && goChapter(previousChapter.no)}>
              <ChevronLeft size={18} />
              <span>{previousChapter ? `上一章：${previousChapter.title.replace(/^第\d+章\s*/, '')}` : '已经是第一章'}</span>
            </button>
            <button className="reader-chapter-button" type="button" disabled={!nextChapter} onClick={() => nextChapter && goChapter(nextChapter.no)}>
              <span>{nextChapter ? `下一章：${nextChapter.title.replace(/^第\d+章\s*/, '')}` : '已经是最后一章'}</span>
              <ChevronRight size={18} />
            </button>
          </div>
        </article>
      </main>

      {catalogOpen && (
        <div className="reader-catalog-layer" role="presentation" onClick={() => setCatalogOpen(false)}>
          <section className="reader-catalog-drawer" role="dialog" aria-modal="true" aria-label="章节目录" onClick={(event) => event.stopPropagation()}>
            <div className="reader-catalog-head">
              <div>
                <span>章节目录</span>
                <strong>{book.title}</strong>
              </div>
              <button type="button" title="关闭目录" onClick={() => setCatalogOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="reader-catalog-summary">
              <span>共 {book.chapters.length} 章</span>
              <em>已读 {readingProgress}%</em>
            </div>
            <div className="reader-catalog-list">
              {book.chapters.map((item, index) => (
                <button
                  className={item.no === chapter.no ? 'active' : ''}
                  type="button"
                  key={item.no}
                  onClick={() => goChapter(item.no)}
                >
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <strong>
                    {item.locked && <LockKeyhole size={14} aria-label="付费章节" />}
                    <span>{item.title.replace(/^第\d+章\s*/, '')}</span>
                  </strong>
                  <em>{item.locked ? `解锁 ${item.price || book.paywall?.chapter_price || 0} 墨水` : `${item.words}字`}</em>
                </button>
              ))}
            </div>
          </section>
        </div>
      )}

      <aside className="reader-tools" aria-label="阅读工具">
        {bookshelfMessage && (
          <div className={`reader-bookshelf-feedback ${bookshelfMessage.type}`} role="status" aria-live="polite">
            {bookshelfMessage.type === 'success' ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}
            <span>{bookshelfMessage.text}</span>
          </div>
        )}
        <button className={inBookshelf ? 'active' : ''} type="button" title={inBookshelf ? '移出书架' : '加入书架'} disabled={bookshelfBusy} onClick={() => void toggleBookshelf()}>
          <span className="reader-shelf-icon"><BookMarked size={19} />{!inBookshelf && <Plus size={11} />}</span>
          <span>{inBookshelf ? '已加入' : '书架'}</span>
        </button>
        <button type="button" title="目录" onClick={() => setCatalogOpen(true)}>
          <Menu size={19} />
          <span>目录</span>
        </button>
        <button type="button" title="夜间模式" onClick={() => setReaderDark((value) => !value)}>
          {readerDark ? <Sun size={19} /> : <Moon size={19} />}
          <span>{readerDark ? '日间' : '夜间'}</span>
        </button>
        <button className={fontPanelOpen ? 'active' : ''} type="button" title="字号" onClick={() => setFontPanelOpen((value) => !value)}>
          <Type size={19} />
          <span>字号</span>
        </button>
        {fontPanelOpen && (
          <div className="reader-font-panel">
            <span>字号</span>
            <button type="button" title="减小字号" disabled={fontLevel === 0} onClick={() => setFontLevel((value) => Math.max(0, value - 1))}>
              <Minus size={18} />
            </button>
            <strong>{16 + fontLevel * 2}</strong>
            <button type="button" title="增大字号" disabled={fontLevel === 4} onClick={() => setFontLevel((value) => Math.min(4, value + 1))}>
              <Plus size={18} />
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};

export default HomePage;
